/**
 * Galeria 3.0 — production port (Sesja 21D.1).
 * Source: prototype LISTFORGE/Galeria 3.0 projekt/Galeria.html + components.jsx + app.jsx.
 * Differences from prototype:
 *   - Real fetch z /api/profiles + /api/gallery/photos + /api/gallery/accounts (zero mock).
 *   - User profiles auto-derived po stronie backendu (src/user_profiles.py + asyncio.Lock).
 *   - Single-file bundle IIFE — eliminuje load-order race miedzy multi-jsx (per GRILL).
 *   - DragSelect: throttle 50ms na mousemove (per GRILL polish #3).
 *   - Native loading="lazy" + content-visibility:auto na thumbnailach (s4 pattern).
 *   - Selection actions sa placeholder toast ("Edytor w Fazie 2") — DONE in scope.
 *
 * Bundling: esbuild (npm run build) → dist/app.bundle.js. React importowany jako ES module,
 * potem IIFE wrapper trzyma scope prywatny. Format=iife w esbuild daje nam single-file output
 * który Chrome ładuje bez module/CORS issues.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
// LF-SAAS-B1 (2026-08-05): kto patrzy + napisy bez nazw dostawców. Wspólne ze Studiem.
import { useWidokKlienta, useWChmurze, drogaPhotoMatch, etykietaFiltra, podpowiedzZrodlaMaski } from './widok-klienta.js';
import { PhotoMatchKlientModal } from './photo-match-klient.jsx';
// Studio na telefonie i tablecie (2026-09-30, plan SAAS/PLAN_studio_telefon_tablet.md): szerokość
// i rodzaj wskaźnika czytane w JEDNYM miejscu; komputer ≥ 1280 px z myszą rysuje się jak dotąd.
import { useUkladEkranu, OknoWysuwane } from './uklad-ekranu.jsx';
// Wspólny sprzątacz przekazań zdjęć do Edytora (porządki LF faza 6) — w paczce, więc zawsze obecny.
import '../shared/przekazanie-zdjec.js';

(function() {
'use strict';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// Układ ekranu dla komponentów w głąb drzewa (przycisk, chip, wiersz listy) bez przeciągania
// propów przez każdy poziom. Wartość domyślna = komputer: dzisiejsze style bez żadnej zmiany.
const UkladKontekst = React.createContext({ telefon: false, waski: false, dotyk: false });
// Pod palcem cel ma mieć co najmniej 44 px (plan §3) — jedna stała zamiast liczby w kilkunastu miejscach.
const CEL_DOTYKU = 44;

/* ---------- MIEJSCE PRZY DOLNEJ KRAWĘDZI (pasek zaznaczenia · pływające przyciski · plakietka podglądu) ----------
 * Plakietka „Widok: Pełny (Ty) / Klient” (`shared/client-mode.js`) jest przyklejona do prawego dolnego rogu OKNA
 * i leży nad wszystkim — w Studiu zasłaniała przyciski paska zaznaczenia i pływającą parę (zmierzone 2026-10-01
 * i 2026-10-06, sprawa `pasek-studia-wlasciciel-2026-10-01-v2`). Studio samo mówi, ile miejsca od dołu zajmuje:
 * każdy element przy dolnej krawędzi zgłasza się tutaj, a plakietka staje 8 px nad najwyższym z nich.
 * Reguła działa TYLKO na stronie Studia (styl wstrzykuje ta paczka) — na pozostałych stronach plakietka stoi jak dotąd.
 * Przyrząd: `tests/przyrzady/studio_pasek/sceny_paska.mjs` (kod 1 = coś znów leży pod plakietką albo poza oknem). */
const ZMIENNA_PLAKIETKI = '--lf-plakietka-dol';
const ZMIENNA_PASKA = '--lf-pasek-wys';
const zajeteDoly = new Map();   // znacznik elementu → funkcja „ile px od dolnej krawędzi okna sięga jego górny brzeg”
function przeliczDol() {
  let najwyzej = 0;
  zajeteDoly.forEach((ile) => { try { najwyzej = Math.max(najwyzej, ile() || 0); } catch (e) { /* element w trakcie odpinania */ } });
  const korzen = document.documentElement;
  if (najwyzej > 0) korzen.style.setProperty(ZMIENNA_PLAKIETKI, `${Math.ceil(najwyzej) + 8}px`);
  else korzen.style.removeProperty(ZMIENNA_PLAKIETKI);
}
if (!document.getElementById('lf-studio-plakietka-css')) {
  const styl = document.createElement('style');
  styl.id = 'lf-studio-plakietka-css';
  styl.textContent = `#lf-client-toggle { bottom: var(${ZMIENNA_PLAKIETKI}, 16px); }`;
  document.head.appendChild(styl);
}
/* `odDolu` podane = element ma animowany `bottom` (wyspa ≥ 1280 px): liczymy z pozycji DOCELOWEJ, nie z klatki
 * animacji. `jakoPasek` = element jest dolnym paskiem < 1280 px: jego wysokość dostają też pływające przyciski. */
function useZajetyDol(ref, { aktywny = true, odDolu = null, jakoPasek = false, klucz = '' } = {}) {
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !aktywny) return undefined;
    const znacznik = {};
    zajeteDoly.set(znacznik, () => {
      if (odDolu == null) return window.innerHeight - el.getBoundingClientRect().top;
      const rodzic = el.offsetParent ? el.offsetParent.getBoundingClientRect().bottom : window.innerHeight;
      return (window.innerHeight - rodzic) + odDolu + el.offsetHeight;
    });
    const odswiez = () => {
      if (jakoPasek) document.documentElement.style.setProperty(ZMIENNA_PASKA, `${el.offsetHeight}px`);
      przeliczDol();
    };
    odswiez();
    const obserwator = typeof ResizeObserver === 'function' ? new ResizeObserver(odswiez) : null;
    if (obserwator) obserwator.observe(el);
    window.addEventListener('resize', odswiez);
    return () => {
      zajeteDoly.delete(znacznik);
      if (obserwator) obserwator.disconnect();
      window.removeEventListener('resize', odswiez);
      if (jakoPasek) document.documentElement.style.removeProperty(ZMIENNA_PASKA);
      przeliczDol();
    };
  }, [ref, aktywny, odDolu, jakoPasek, klucz]);
}
/* Pływający przycisk (albo para) przy dolnej krawędzi — zgłasza swoją wysokość, żeby plakietka stanęła nad nim. */
function NadDolem({ style, children }) {
  const ref = useRef(null);
  useZajetyDol(ref);
  return <div ref={ref} style={style}>{children}</div>;
}

/* ---------- API ---------- */

async function apiGet(path) {
  // Sesja 21D.3: cache: 'no-store' wymusza refetch z serwera za każdym razem.
  // Bez tego browser cachował /api/gallery/photos przez 30s (backend max-age=30) →
  // po Apply w edytorze + wróceniu do galerii, operator widział stare thumb_path
  // z starym t= → cached thumby mimo świeżego bake. Hard refresh obchodził problem,
  // ale normalna nawigacja ← Galeria pokazywała stale. Backend cache (TTL 30s)
  // nadal działa — fetch jest szybki bo response z cache backend.
  const r = await fetch(path, {cache: 'no-store'});
  if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + path);
  return r.json();
}

async function apiSend(path, method, body) {
  const r = await fetch(path, {
    method,
    headers: {'Content-Type': 'application/json'},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    let msg = 'HTTP ' + r.status;
    try { const j = await r.json(); if (j.detail) msg += ' — ' + j.detail; } catch {}
    throw new Error(msg);
  }
  return r.json();
}

// Limit pozycji „Usuń” części zdjęć na jedno żądanie = `_USUN_ZDJECIA_MAX` serwera (api_server.py; w chmurze 422 powyżej).
const USUN_ZDJECIA_PORCJA = 100;

/* ---------- ICONS ---------- */
const Icon = {
  Check: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><path d="M3 8.5 L6.5 12 L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  X: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><path d="M4 4 L12 12 M12 4 L4 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  Grid: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><rect x="2" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1.3"/><rect x="9" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1.3"/><rect x="2" y="9" width="5" height="5" stroke="currentColor" strokeWidth="1.3"/><rect x="9" y="9" width="5" height="5" stroke="currentColor" strokeWidth="1.3"/></svg>,
  Edit: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><path d="M2 14 L5 13 L13 5 L11 3 L3 11 Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  Trash: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><path d="M3 5 L13 5 M6 5 L6 3 L10 3 L10 5 M4.5 5 L5 13.5 L11 13.5 L11.5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  Download: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><path d="M8 2 L8 10 M4.5 6.5 L8 10 L11.5 6.5 M2.5 13 L13.5 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  Upload: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><path d="M8 13 L8 5 M4.5 8.5 L8 5 L11.5 8.5 M2.5 3 L13.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  Plus: (p) => <svg viewBox="0 0 16 16" width={p.size||12} height={p.size||12} fill="none"><path d="M8 3 L8 13 M3 8 L13 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  Chevron: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none" style={{transform:`rotate(${p.rot||0}deg)`,transition:'transform 0.15s'}}><path d="M5 3 L10 8 L5 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  Home: (p) => <svg viewBox="0 0 20 20" width={p.size||16} height={p.size||16} fill="none"><path d="M3 9 L10 3 L17 9 L17 16 L12 16 L12 11 L8 11 L8 16 L3 16 Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  // LF-PHOTOROOM-EDYTOR (2026-07-29): brakujące ogniwo między „Eksport" i „Podmień" —
  // folder, do którego operator wkleja pliki/ZIP-y pobrane z Photoroom.
  Folder: (p) => <svg viewBox="0 0 16 16" width={p.size||14} height={p.size||14} fill="none"><path d="M2 4.5 L2 12.5 L14 12.5 L14 6 L7.5 6 L6.3 4.5 Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>,
};

/* ---------- HELPERS ---------- */

// LF-MASK-FILTERS (2026-06-12): filtry po STANIE maski (mask_source). Poprzednia wersja
// (LF-PHOTOROOM-FILTER 2026-06-08) znała tylko photoroom: 'pr_done'==='photoroom',
// 'pr_raw'!=='photoroom'. Po przejściu na BiRefNet (lokalny) + Pixian to się sypało —
// "Po Photoroom" łapało 0 zdjęć, "Surowe" łapało WSZYSTKO (w tym gotowe lokalne/pixian).
// mask_source ma 4 wartości: 'none'(surowe) | 'local'(dziś Apple Vision) | 'pixian' |
// 'photoroom'(legacy). Batch miesza local+pixian (guard metek → auto-Pixian), stąd jeden
// wspólny chip "Lokalne + Pixian" + osobny "Pixian" (operator: widzieć płatne osobno).
//   'raw'          = mask_source==='none'                      → surowe, do przerobienia
//   'local_pixian' = mask_source IN ('local','pixian')         → przerobione batchem
//   'pixian'       = mask_source==='pixian'                     → tylko Pixian (płatne)
//   'photoroom'    = mask_source==='photoroom'                  → legacy Photoroom
// LF-PIXELCUT-SILNIK (2026-08-05): doszło piąte źródło — 'pixelcut'. Bez dopisania go tutaj
// zdjęcie wycięte w chmurze nie pasowałoby do ŻADNEGO chipa i zniknęłoby ze wszystkich
// filtrów — czyli najdroższa maska w systemie byłaby jedyną niewidoczną.
// Świadomie OSOBNY chip, nie doklejenie do „Pixian": Pixelcut kosztuje 6× tyle, więc
// pytanie „gdzie poszły pieniądze" ma dać się zadać wprost. Wspólna etykieta oszczędziłaby
// jednego kafelka i skasowała tę odpowiedź.
const TAGS = ['raw', 'local_pixian', 'pixian', 'pixelcut', 'photoroom'];
const TAG_LABEL = {raw:'Surowe', local_pixian:'Lokalne + płatne', pixian:'Pixian',
                   pixelcut:'Pixelcut', photoroom:'Po Photoroom'};
const TAG_MATCHERS = {
  raw:          (ms) => ms === 'none',
  local_pixian: (ms) => ms === 'local' || ms === 'pixian' || ms === 'pixelcut',
  pixian:       (ms) => ms === 'pixian',
  pixelcut:     (ms) => ms === 'pixelcut',
  photoroom:    (ms) => ms === 'photoroom',
};

function throttle(fn, ms) {
  let last = 0, timer = null, lastArgs = null;
  return function(...args) {
    const now = Date.now();
    const remain = ms - (now - last);
    lastArgs = args;
    if (remain <= 0) {
      last = now;
      fn.apply(this, args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        fn.apply(this, lastArgs);
      }, remain);
    }
  };
}

function colorFromString(s) {
  // Stable color hash for listing label swatch
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h<<5) - h + s.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 45% 55%)`;
}

const UNKNOWN_PROFILE_ID = 'u_unknown';

// Listing/photo "Bez konta" matching: empty string OR literal 'Bez konta' label
// (cache_utils returns empty string for missing _vinted_account, gallery/accounts
//  endpoint normalizes to 'Bez konta' label — we accept both).
function isUnassignedAccount(account) {
  return !account || account === 'Bez konta';
}

function profileMatchesAccount(profile, account) {
  if (profile.id === UNKNOWN_PROFILE_ID) return isUnassignedAccount(account);
  return (profile.vinted_accounts || []).includes(account);
}

/* ---------- SIDEBAR ---------- */

function ProfileSwatch({ color }) {
  return <span style={{width:10, height:10, borderRadius:3, background:color, display:'inline-block', border:'1px solid var(--border-2)'}}/>;
}

function SideRow({ icon, label, count, active, onClick, indent }) {
  const { dotyk } = React.useContext(UkladKontekst);
  return (
    <button onClick={onClick} style={{
      width:'100%', textAlign:'left',
      background: active ? 'var(--panel-2)' : 'transparent',
      border:'none', borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
      padding: indent ? '6px 14px 6px 42px' : '7px 14px',
      display:'flex', alignItems:'center', gap:9,
      cursor:'pointer', color:'var(--text)', transition:'background 0.12s',
      ...(dotyk ? { minHeight: CEL_DOTYKU } : null),
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{color: active?'var(--accent-text)':'var(--text-2)', display:'flex', alignItems:'center', width:14, justifyContent:'center'}}>{icon}</span>
      <span style={{flex:1, minWidth:0, overflow:'hidden'}}>
        <div style={{fontSize:12.5, fontWeight: active?500:400, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{label}</div>
      </span>
      {count !== undefined && <span style={{fontSize:11, color:'var(--text-3)', fontVariantNumeric:'tabular-nums'}}>{count}</span>}
    </button>
  );
}

function SideSection({ title, action, children }) {
  return (
    <div style={{padding:'14px 0 4px'}}>
      <div style={{padding:'0 16px 6px', display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:10.5, color:'var(--text-3)', letterSpacing:0.8, textTransform:'uppercase', fontWeight:600}}>
        <span>{title}</span>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Sidebar({ profiles, listings, photoCounts, expandedProfileIds, onToggleProfile, activeProfileId, activeListingId, onAllClick, onProfileClick, onListingClick, onDeleteProfile, batches, activeBatchKey, onBatchClick, wSzufladzie = false, onZamknij, widokKlienta = false }) {
  const { dotyk } = React.useContext(UkladKontekst);
  const total = Object.values(photoCounts).reduce((a,b)=>a+b, 0);
  // Pod palcem: link i zamknięcie szuflady jako cele ≥ 44 px (komputer: dzisiejszy link bez zmian).
  const celNaglowka = dotyk || wSzufladzie ? { minHeight: CEL_DOTYKU, minWidth: CEL_DOTYKU, padding: '0 8px', justifyContent: 'center' } : null;
  return (
    <aside style={{
      width:260, minWidth:260, background:'var(--bg-2)',
      borderRight:'1px solid var(--line)',
      display:'flex', flexDirection:'column', overflow:'hidden',
      // Szuflada (ekran < 1280 px): szerokość i wysokość daje okno wysuwane.
      ...(wSzufladzie ? { width:'100%', minWidth:0, height:'100%', borderRight:'none' } : null),
    }}>
      <div style={{padding:'16px 16px 14px', borderBottom:'1px solid var(--line)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <div style={{fontSize:20, fontWeight:700, letterSpacing:-0.3}}>Studio</div>
        {/* P4 (2026-10-02, nowy wygląd): dawny link „Start” zdjęty — Start ma menu u góry (shared/menu.js). */}
        {wSzufladzie && (
          <button onClick={onZamknij} aria-label="Zamknij listę kont" style={{background:'transparent', border:'1px solid var(--line)', borderRadius:8, color:'var(--text-2)', cursor:'pointer', display:'inline-flex', alignItems:'center', ...celNaglowka}}>
            <Icon.X size={14}/>
          </button>
        )}
      </div>

      <div style={{flex:1, overflow:'auto'}}>
        <SideSection title="Biblioteka">
          <SideRow icon={<Icon.Grid/>} label="Wszystkie zdjęcia" count={total} active={activeProfileId === null && activeListingId === null} onClick={onAllClick}/>
        </SideSection>

        {/* Nieczynny „+” (zapowiedź tworzenia profilu) znika pod palcem i w szufladzie: przycisk,
            który nic nie robi, nie zasługuje na cel 44 px na małym ekranie. Komputer bez zmian.
            B119 (raport B117 pkt 7): u KLIENTA znika zawsze — jego dymek „…w Fazie 1.5” to nasz plan prac,
            nie funkcja (makieta 6: „+” zniknął). Operator bez zmian. */}
        <SideSection title="Profile" action={dotyk || wSzufladzie || widokKlienta ? null : <button title="Tworzenie profilu w Fazie 1.5" disabled style={{background:'transparent', border:'1px solid var(--line)', color:'var(--text-3)', width:20, height:20, borderRadius:4, cursor:'not-allowed', padding:0, opacity:0.5}}><Icon.Plus size={12}/></button>}>
          {profiles.map(prof => {
            const isExpanded = expandedProfileIds.has(prof.id);
            const profileListings = listings.filter(l => profileMatchesAccount(prof, l.account));
            const profCount = profileListings.reduce((s,l) => s + (photoCounts[l.id]||0), 0);
            const isUnknown = prof.id === UNKNOWN_PROFILE_ID;
            return (
              <div key={prof.id} className="profile-row" style={{position:'relative'}}>
                <button onClick={() => onToggleProfile(prof.id)} onDoubleClick={() => onProfileClick(prof.id)} style={{
                  width:'100%', textAlign:'left',
                  background: activeProfileId === prof.id ? 'var(--panel-2)' : 'transparent',
                  border:'none', borderLeft: activeProfileId === prof.id ? '2px solid var(--accent)' : '2px solid transparent',
                  padding:'8px 14px', display:'flex', alignItems:'center', gap:8,
                  cursor:'pointer', color:'var(--text)',
                  // pod palcem miejsce na zawsze widoczny kosz (44 px) po prawej
                  ...(dotyk ? { minHeight: CEL_DOTYKU + 8, paddingRight: CEL_DOTYKU + 14 } : null),
                }}>
                  <span style={{color:'var(--text-2)', display:'flex', alignItems:'center', width:10}}>
                    <Icon.Chevron rot={isExpanded ? 90 : 0} size={12}/>
                  </span>
                  {/* P4: jednolity akcent zamiast przejścia w fiolet — napis --on-accent ma ≥ 4,5:1 w obu
                      motywach na samym akcencie, a na jasnym fiolecie (#c792ea) biel dawała ~2,3:1. */}
                  <span style={{
                    width:22, height:22, borderRadius:'50%',
                    background:'var(--accent)',
                    color:'var(--on-accent)', fontSize:10, fontWeight:700,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>{prof.name.slice(0,2).toUpperCase()}</span>
                  <span style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:12.5, fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{prof.name}</div>
                    <div style={{fontSize:10.5, color:'var(--text-3)'}}>{profileListings.length} ogł. · {profCount} zdj.</div>
                  </span>
                </button>
                {!isUnknown && onDeleteProfile && (
                  <button
                    className="profile-trash"
                    title="Usuń profil i całą zawartość"
                    onClick={(e) => { e.stopPropagation(); onDeleteProfile(prof, profileListings, profCount); }}
                    style={{
                      position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
                      width:24, height:24, borderRadius:4,
                      background:'transparent', border:'1px solid var(--line)',
                      color:'var(--text-3)', cursor:'pointer',
                      display:'none', alignItems:'center', justifyContent:'center', padding:0,
                      // Pod palcem kosz jest widoczny zawsze (CSS `hover: none` w index.html) — i ma cel 44 px.
                      ...(dotyk ? { width: CEL_DOTYKU, height: CEL_DOTYKU, borderRadius: 8 } : null),
                    }}
                  >
                    <Icon.Trash size={12}/>
                  </button>
                )}
                {isExpanded && profileListings.map(l => (
                  <SideRow key={l.id}
                    indent
                    icon={<ProfileSwatch color={colorFromString(l.id)}/>}
                    label={l.label}
                    count={photoCounts[l.id] || 0}
                    active={activeListingId === l.id}
                    onClick={() => onListingClick(l.id)}
                  />
                ))}
              </div>
            );
          })}
          {profiles.length === 0 && (
            <div style={{padding:'8px 16px', fontSize:11, color:'var(--text-3)'}}>{widokKlienta
              /* B119 (raport B117 pkt 7): klient nie zna „LFH” — mówimy, skąd biorą się konta (makieta 6). */
              ? 'Nie masz jeszcze kont — pobierz ogłoszenia z Vinted wtyczką Selna, pojawią się tutaj.'
              : 'Brak profili — załaduj listingi przez LFH'}</div>
          )}
        </SideSection>

        {batches && batches.length > 0 && (
          <SideSection title="Partia (import)">
            <SideRow icon={<Icon.Grid/>} label="Wszystkie partie" count={batches.reduce((s,b)=>s+b.count,0)} active={!activeBatchKey} onClick={() => onBatchClick(null)}/>
            {batches.map(b => (
              <SideRow key={b.id}
                indent
                icon={<ProfileSwatch color={colorFromString(b.id)}/>}
                label={b.label}
                count={b.count}
                active={activeBatchKey === b.id}
                onClick={() => onBatchClick(b.id)}
              />
            ))}
          </SideSection>
        )}
      </div>

      <div style={{padding:'12px 16px', borderTop:'1px solid var(--line)', fontSize:11, color:'var(--text-3)', display:'flex', justifyContent:'space-between'}}>
        <span>{total} zdjęć · {profiles.length} profili</span>
        {/* Z32 (tura 90 Ł, łowca Ł9): stała kropka „Ready” nic nie mierzy, a u klienta to angielski napis — tylko maszynownia. */}
        {!widokKlienta && <span style={{color:'var(--ok)'}}>● Ready</span>}
      </div>
    </aside>
  );
}

/* ---------- TOOLBAR ---------- */

function Chip({ active, onClick, children }) {
  const { waski, dotyk } = React.useContext(UkladKontekst);
  return (
    <button onClick={onClick} aria-pressed={active} style={{
      background: active ? 'var(--accent)' : 'var(--panel)',
      border: '1px solid ' + (active ? 'var(--accent)' : 'var(--line)'),
      color: active ? 'var(--on-accent)' : 'var(--text)',
      fontSize:12, fontWeight: active?600:500,
      padding:'5px 11px', borderRadius:999, cursor:'pointer',
      fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:5,
      transition:'all 0.12s',
      // wąski ekran: chipy w jednym przewijanym rzędzie — nie ściskają się i nie łamią napisu
      ...(waski ? { flex:'0 0 auto', whiteSpace:'nowrap' } : null),
      ...(dotyk ? { minHeight: CEL_DOTYKU, padding:'0 14px', fontSize:13 } : null),
    }}>
      {active && <Icon.Check size={11}/>}
      {children}
    </button>
  );
}

function Toolbar({ activeTags, onToggleTag, onClearTags, thumbSize, onThumbSize, viewTitle, viewSub, widokKlienta = false, onOtworzKonta, przyciskKontRef }) {
  const { telefon, waski, dotyk } = React.useContext(UkladKontekst);
  if (waski) {
    // < 1280 px: lista kont jest w szufladzie (przycisk ☰), filtry w jednym przewijanym rzędzie.
    // Suwak wielkości miniatur znika na telefonie — tam siatka ma zawsze 3 kolumny.
    const cel = { minWidth: CEL_DOTYKU, minHeight: CEL_DOTYKU, display:'inline-flex', alignItems:'center', justifyContent:'center' };
    return (
      <div style={{display:'flex', flexDirection:'column', borderBottom:'1px solid var(--line)', background:'var(--bg-2)'}}>
        <div style={{display:'flex', alignItems:'center', gap:8, padding: telefon ? '4px 8px' : '6px 14px'}}>
          {/* P4 (nowy wygląd): ☰ dostaje napis „Konta” — menu u góry ma swój przycisk „Menu”, więc dwa
              gołe ☰ na jednym ekranie byłyby zagadką „który co otwiera” (jeden właściciel ☰ — plan §4 P4). */}
          <button ref={przyciskKontRef} onClick={onOtworzKonta} aria-label="Konta i partie" aria-haspopup="dialog"
            style={{...cel, gap:6, background:'var(--panel)', border:'1px solid var(--line)', borderRadius:10, color:'var(--text)', cursor:'pointer', padding:'0 12px 0 10px', fontSize:13, fontWeight:600, fontFamily:'inherit', flex:'0 0 auto'}}>
            <svg viewBox="0 0 16 16" width="18" height="18" fill="none" aria-hidden="true"><path d="M2.5 4 L13.5 4 M2.5 8 L13.5 8 M2.5 12 L13.5 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            Konta
          </button>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:15, fontWeight:600, letterSpacing:-0.1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{viewTitle}</div>
            {/* --text-2, nie --text-3: nowy napis nie dokłada błędu kontrastu (pa11y 390 px, WCAG AA) */}
            <div style={{fontSize:11, color:'var(--text-2)', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{viewSub}</div>
          </div>
          {!telefon && (
            <input type="range" min="100" max="260" step="10" value={thumbSize} onChange={e=>onThumbSize(+e.target.value)}
              aria-label="Wielkość miniatur" style={{width:90, accentColor:'var(--accent)', ...(dotyk ? { height: CEL_DOTYKU } : null)}}/>
          )}
          {/* P4: dawny przycisk ⌂ „Strona startowa” zdjęty — Start jest w menu u góry. */}
        </div>
        <div data-przewijany-poziomo style={{display:'flex', alignItems:'center', gap:8, padding: telefon ? '0 8px 8px' : '0 14px 8px', overflowX:'auto', scrollbarWidth:'none'}}>
          {TAGS.map(t => (
            <Chip key={t} active={activeTags.includes(t)} onClick={()=>onToggleTag(t)}>{etykietaFiltra(t, widokKlienta)}</Chip>
          ))}
          {activeTags.length > 0 && (
            <button onClick={onClearTags} style={{...cel, flex:'0 0 auto', whiteSpace:'nowrap', background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', fontSize:12, gap:4, padding:'0 8px'}}>
              <Icon.X size={10}/> wyczyść ({activeTags.length})
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div style={{display:'flex', flexDirection:'column', borderBottom:'1px solid var(--line)', background:'var(--bg-2)'}}>
      <div style={{display:'flex', alignItems:'center', gap:12, padding:'10px 18px'}}>
        <div style={{flex:'0 0 auto'}}>
          <div style={{fontSize:15, fontWeight:600, letterSpacing:-0.1}}>{viewTitle}</div>
          <div style={{fontSize:11, color:'var(--text-3)', marginTop:1}}>{viewSub}</div>
        </div>
        <div style={{flex:1}}/>
        <div style={{display:'flex', alignItems:'center', gap:8, color:'var(--text-3)'}}>
          <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor"><rect x="2" y="2" width="4" height="4"/><rect x="8" y="2" width="3" height="3"/><rect x="12" y="2" width="2" height="2"/></svg>
          <input type="range" min="120" max="260" step="10" value={thumbSize} onChange={e=>onThumbSize(+e.target.value)} style={{width:90, accentColor:'var(--accent)', ...(dotyk ? { height: CEL_DOTYKU } : null)}}/>
          <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><rect x="2" y="2" width="6" height="6"/><rect x="9" y="2" width="5" height="5"/></svg>
        </div>
      </div>
      <div style={{display:'flex', alignItems:'center', gap:8, padding:'0 18px 10px', flexWrap:'wrap'}}>
        <span style={{fontSize:11, color:'var(--text-3)', letterSpacing:0.5, textTransform:'uppercase', fontWeight:600, marginRight:4}}>Filtr</span>
        {TAGS.map(t => (
          <Chip key={t} active={activeTags.includes(t)} onClick={()=>onToggleTag(t)}>{etykietaFiltra(t, widokKlienta)}</Chip>
        ))}
        {activeTags.length > 0 && (
          <button onClick={onClearTags} style={{background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', fontSize:11, marginLeft:4, display:'inline-flex', alignItems:'center', gap:3, ...(dotyk ? { minHeight: CEL_DOTYKU } : null)}}>
            <Icon.X size={10}/> wyczyść ({activeTags.length})
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- PHOTO CARD ---------- */

function PhotoCard({ photo, selected, onClick, size, showProfileBadge, widokKlienta = false }) {
  const { dotyk } = React.useContext(UkladKontekst);
  // Sesja 22AU (2026-05-09): galeria respektuje bg_applied — gdy False (po Photoroom
  // transparent lub po restore_original), pokaż garment.png + szachownica zamiast
  // result.jpg. Konsystentne z edytorem (też pokazuje szachownicę gdy bg_applied=false).
  const showTransparent = photo.bg_applied === false && photo.garment_path;
  const tileSrc = showTransparent ? photo.garment_path : (photo.thumb_path || photo.photo_path);
  return (
    <div
      data-photo-id={photo.id}
      onClick={(e) => onClick(e, photo)}
      className="thumb-card"
      style={{
        position:'relative', cursor:'default', userSelect:'none',
        borderRadius:8,
        outline: selected ? '2px solid var(--select-border)' : '2px solid transparent',
        outlineOffset:2,
        transition:'outline 0.08s, transform 0.1s',
        transform: selected ? 'scale(0.98)' : 'scale(1)',
        // Pod palcem: bez opóźnienia i powiększania dwuklikiem (Safari), bez menu „zapisz obraz”
        // przy przytrzymaniu — dotknięcie ma zaznaczać (plan §3).
        ...(dotyk ? { touchAction:'manipulation', WebkitTouchCallout:'none', WebkitTapHighlightColor:'transparent' } : null),
      }}
    >
      <div
        className={showTransparent ? 'checker-bg' : ''}
        style={{
          position:'relative', aspectRatio:'3 / 4',
          // P4: powierzchnia POD zdjęciem neutralna i niezależna od motywu (plan §7) — jasny motyw nie
          // może zmieniać oceny wyniku; widać ją tylko przy ładowaniu albo przy zdjęciu, które nie wstało.
          background: showTransparent ? undefined : 'var(--pod-podgladem)',
          borderRadius:6, overflow:'hidden',
          boxShadow: selected ? '0 0 0 2px var(--accent-soft), 0 8px 24px var(--accent-soft)' : '0 1px 2px rgba(0,0,0,0.3)',
        }}>
        <img
          src={tileSrc}
          loading="lazy"
          decoding="async"
          alt={photo.product_label}
          style={{
            width:'100%', height:'100%',
            objectFit: showTransparent ? 'contain' : 'cover',
            display:'block',
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        {selected && <div style={{position:'absolute', inset:0, background:'var(--select)', pointerEvents:'none'}}/>}

        {/* Tag badge bottom-left */}
        {photo.tag && photo.tag !== 'bez_tagu' && (
          <div style={{position:'absolute', bottom:6, left:6, background:'var(--na-zdjeciu-tlo)', color:'var(--na-zdjeciu-napis)', fontSize:9.5, padding:'2px 6px', borderRadius:3, backdropFilter:'blur(4px)', textTransform:'uppercase', letterSpacing:0.4, fontWeight:600}}>
            {TAG_LABEL[photo.tag] || photo.tag}
          </div>
        )}

        {/* QC issue indicator */}
        {photo.has_issue && (
          <div title={`QC score: ${photo.qc_score}`} style={{position:'absolute', bottom:6, right:6, width:8, height:8, borderRadius:'50%', background:'var(--warn)', boxShadow:'0 0 0 1.5px rgba(15,15,16,0.6)'}}/>
        )}

        {/* Sesja 22AW + LF-MASK-FILTERS (2026-06-12): wskaźnik źródła maski (kropka).
            Operator widzi skad jest maska bez otwierania edytora. Photoroom (fiolet) i
            Pixian (cyjan) wykluczają się wzajemnie → ten sam slot. Auto-shift w lewo
            o 14px gdy QC dot obecny zeby sie nie nakladaly. */}
        {/* LF-PIXELCUT-SILNIK (2026-08-05): trzeci kolor w tym samym slocie — bursztyn dla
            Pixelcuta. Osobny kolor, bo osobna cena: kropka ma pozwolić przelecieć wzrokiem
            po siatce i zobaczyć, ile zdjęć poszło najdroższą drogą. */}
        {['photoroom', 'pixian', 'pixelcut'].includes(photo.mask_source) && (
          <div
            /* LF-SAAS-B1 (2026-08-05): treść podpowiedzi z jednego źródła — u klienta
               bez nazwy dostawcy i bez naszej stawki w złotówkach. Kropka i jej KOLOR
               zostają: to informacja o zdjęciu, nie o naszej maszynowni. */
            title={podpowiedzZrodlaMaski(photo.mask_source, widokKlienta)}
            style={{
              position:'absolute', bottom:6,
              right: photo.has_issue ? 22 : 6,
              width:8, height:8, borderRadius:'50%',
              background: photo.mask_source === 'pixian' ? '#06b6d4'
                        : photo.mask_source === 'pixelcut' ? '#f59e0b' : '#a855f7',
              boxShadow:'0 0 0 1.5px rgba(15,15,16,0.6)',
            }}
          />
        )}

        {/* Check badge top-right */}
        {dotyk ? (
          /* Pod palcem: kółko 26 px w polu 44 px w rogu kafelka (plan §4 1b). Samo dotknięcie
             kafelka też przełącza — kółko zostaje, bo mówi, że zdjęcie jest zaznaczalne. */
          <div
            role="checkbox" aria-checked={selected} aria-label="Zaznacz zdjęcie"
            onClick={(e) => { e.stopPropagation(); onClick({...e, metaKey:true}, photo); }}
            /* +2 px: zaznaczony kafelek skaluje się do 0,98 — pole ma zostać ≥ 44 px także wtedy */
            style={{position:'absolute', top:0, right:0, width:CEL_DOTYKU + 2, height:CEL_DOTYKU + 2, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'}}
          >
            <div style={{
              width:26, height:26, borderRadius:'50%',
              background: selected ? 'var(--accent)' : 'rgba(15,15,16,0.55)',
              border: selected ? 'none' : '1.5px solid rgba(255,255,255,0.8)',
              color: selected ? 'var(--on-accent)' : 'transparent',
              display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none',
            }}>
              {selected && <Icon.Check size={15}/>}
            </div>
          </div>
        ) : (
        <div
          onClick={(e) => { e.stopPropagation(); onClick({...e, metaKey:true}, photo); }}
          style={{
            position:'absolute', top:6, right:6, width:20, height:20, borderRadius:'50%',
            background: selected ? 'var(--accent)' : 'rgba(15,15,16,0.55)',
            border: selected ? 'none' : '1.5px solid rgba(255,255,255,0.7)',
            backdropFilter:'blur(4px)',
            color: selected ? 'var(--on-accent)' : 'transparent',
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
            transition:'all 0.12s',
          }}
        >
          {selected && <Icon.Check size={12}/>}
        </div>
        )}

        {/* Profile badge top-left (only when "Wszystkie" view) */}
        {showProfileBadge && photo.account && (
          <div style={{position:'absolute', top:6, left:6, background:'var(--na-zdjeciu-tlo)', color:'var(--na-zdjeciu-napis)', fontSize:9.5, padding:'2px 6px', borderRadius:3, backdropFilter:'blur(4px)', maxWidth:'70%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
            {photo.account}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- GROUP HEADER (per-listing section) ---------- */

function GroupHeader({ listing, count, selectedCount, onSelectAll, collapsed, onToggle }) {
  const { telefon, dotyk } = React.useContext(UkladKontekst);
  const cel = dotyk ? { minWidth: CEL_DOTYKU, minHeight: CEL_DOTYKU, justifyContent:'center' } : null;
  const liczba = selectedCount > 0 ? <><b style={{color:'var(--accent-text)'}}>{selectedCount}</b> / {count}</> : `${count} zdjęć`;
  // Z32 (tura 90 Ł, łowca Ł4): podwójny klik = DWA kliknięcia — pierwsze zaznaczało grupę, drugie (napis już
  // „Odznacz grupę”) odznaczało ją całą RAZEM z tym, co było zaznaczone wcześniej (2 → 0). Liczy się tylko
  // pierwsze kliknięcie serii (`detail` 1); klawiatura i czytnik ekranu (`detail` 0) działają jak dotąd.
  const klikGrupy = (e) => { if (e && e.detail > 1) return; onSelectAll(); };
  if (telefon) {
    // Telefon: nazwa i konto w dwóch linijkach obok siebie się nie mieszczą — jedna kolumna tekstu.
    return (
      <div style={{display:'flex', alignItems:'center', gap:6, padding:'2px 0 8px', borderBottom:'1px solid var(--line)', marginBottom:8}}>
        <button onClick={onToggle} aria-label={collapsed ? 'Rozwiń ogłoszenie' : 'Zwiń ogłoszenie'} aria-expanded={!collapsed}
          style={{background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', padding:0, display:'flex', alignItems:'center', minWidth:CEL_DOTYKU, minHeight:CEL_DOTYKU, justifyContent:'center'}}>
          <Icon.Chevron rot={collapsed ? 0 : 90}/>
        </button>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13.5, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={listing.label}>{listing.label}</div>
          <div style={{fontSize:11, color:'var(--text-2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
            {listing.account || 'Bez konta'} · {liczba}
          </div>
        </div>
        <button onClick={klikGrupy} style={{background:'var(--panel)', border:'1px solid var(--line)', color:'var(--text)', fontSize:12, padding:'0 10px', borderRadius:8, cursor:'pointer', fontFamily:'inherit', minHeight:CEL_DOTYKU, flex:'0 0 auto'}}>
          {selectedCount === count ? 'Odznacz' : 'Zaznacz'}
        </button>
      </div>
    );
  }
  return (
    <div style={{
      gridColumn:'1 / -1',
      display:'flex', alignItems:'center', gap:10,
      padding:'4px 0 10px', borderBottom:'1px solid var(--line)', marginBottom:14,
    }}>
      <button onClick={onToggle} style={{background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', padding:2, display:'flex', alignItems:'center', ...cel}}>
        <Icon.Chevron rot={collapsed ? 0 : 90}/>
      </button>
      <ProfileSwatch color={colorFromString(listing.id)}/>
      <div style={{fontSize:14, fontWeight:600, maxWidth:'40%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={listing.label}>{listing.label}</div>
      <div style={{fontSize:11, color:'var(--text-3)', fontFamily:'JetBrains Mono, monospace'}}>{listing.account || 'Bez konta'}</div>
      <div style={{flex:1}}/>
      <div style={{fontSize:11.5, color:'var(--text-3)'}}>
        {liczba}
      </div>
      <button onClick={klikGrupy} style={{background:'var(--panel)', border:'1px solid var(--line)', color:'var(--text)', fontSize:11, padding:'3px 9px', borderRadius:5, cursor:'pointer', fontFamily:'inherit', ...(dotyk ? { minHeight: CEL_DOTYKU, padding:'0 12px', fontSize:12 } : null)}}>
        {selectedCount === count ? 'Odznacz grupę' : 'Zaznacz grupę'}
      </button>
    </div>
  );
}

/* ---------- SELECTION BAR (floating bottom) ---------- */

function BarBtn({ onClick, icon, label, hint, primary, danger, disabled }) {
  const { waski, dotyk } = React.useContext(UkladKontekst);
  const base = {
    background:'transparent', border:'1px solid transparent',
    color:'var(--text)', fontSize:12.5, fontWeight:500,
    padding:'6px 10px', borderRadius:7, cursor:'pointer',
    display:'inline-flex', alignItems:'center', gap:6,
    fontFamily:'inherit', whiteSpace:'nowrap',
    transition:'background 0.1s, border-color 0.1s',
  };
  let style = {...base};
  if (primary) style = {...style, background:'var(--accent)', color:'var(--on-accent)', fontWeight:600};
  if (danger) style = {...style, color:'var(--danger-text)'};
  // LF-PHOTOROOM-EDYTOR: `disabled` opcjonalny (default false) — istniejące wywołania
  // (Eksport/Usuń/...) nie musiały się zmieniać, dostał go tylko nowy przycisk „Folder".
  if (disabled) style = {...style, opacity: 0.5, cursor: 'not-allowed'};
  // Pod palcem cel ≥ 44 px; w jednym rzędzie wąskiego paska przycisk się nie ściska.
  if (dotyk) style = {...style, minHeight: CEL_DOTYKU, padding: waski ? '0 9px' : '0 12px'};
  if (waski) style = {...style, flex: '0 0 auto'};
  return (
    <button onClick={onClick} disabled={disabled} style={style}
      onMouseEnter={e => {
        if (disabled) return;
        if (!primary && !danger) e.currentTarget.style.background = 'var(--surface-3)';
        if (primary) e.currentTarget.style.background = 'var(--accent-2)';
        if (danger) e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) 10%, transparent)';
      }}
      onMouseLeave={e => {
        if (disabled) return;
        if (!primary && !danger) e.currentTarget.style.background = 'transparent';
        if (primary) e.currentTarget.style.background = 'var(--accent)';
        if (danger) e.currentTarget.style.background = 'transparent';
      }}
    >
      {icon} {label}
      {hint && <kbd style={{
        fontFamily:'JetBrains Mono, monospace', fontSize:10,
        background: primary ? 'rgba(0,0,0,0.15)' : 'var(--panel-2)',
        border: '1px solid ' + (primary ? 'rgba(0,0,0,0.2)' : 'var(--line)'),
        color: primary ? 'var(--on-accent)' : 'var(--text-3)',
        padding:'1px 5px', borderRadius:3, marginLeft:4,
      }}>{hint}</kbd>}
    </button>
  );
}

/* Pozycja wysuwanego okna „Więcej” (telefon) — cały wiersz jest celem, 52 px wysokości. */
function PozycjaWiecej({ icon, label, onClick, disabled, danger }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width:'100%', minHeight:52, padding:'0 20px', display:'flex', alignItems:'center', gap:12,
      background:'transparent', border:'none', borderBottom:'1px solid var(--line)', textAlign:'left',
      color: danger ? 'var(--danger-text)' : 'var(--text)', fontSize:15, fontFamily:'inherit',
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
    }}>
      <span style={{width:20, display:'inline-flex', justifyContent:'center'}}>{icon}</span>{label}
    </button>
  );
}

function SelectionBar({ count, totalVisible, onEdit, onDelete, onDeselect, onExport, onPhotoMatch, onPodmienOryginaly, onWybielTlo, onOtworzFolderPhotoroom, photoroomFolderBusy, onPhotoroomImport, widokKlienta = false, podmianaPrzegladarka = false, onPobierzDoObrobki, onWgrajGotowe, paczkaBusy = false, maPaczke = false, wiecejOtwarte = false, onWiecej }) {
  const uklad = React.useContext(UkladKontekst);
  const wiecejRef = useRef(null);
  // Pasek zgłasza, ile miejsca zajmuje przy dolnej krawędzi (plakietka podglądu i pływające przyciski stają nad nim).
  const pasekRef = useRef(null);
  useZajetyDol(pasekRef, {
    aktywny: uklad.waski ? totalVisible > 0 : count > 0,
    odDolu: uklad.waski ? null : 18,
    jakoPasek: uklad.waski,
    klucz: uklad.telefon ? 'telefon' : (uklad.waski ? 'waski' : 'wyspa'),
  });
  // ── Ekran < 1280 px (plan §4 1b, 1h): pasek stoi na dole ZAWSZE, gdy są zdjęcia — przy zerze
  //    zaznaczonych mówi, co zrobić, a działania są wyłączone. Pusty katalog = bez paska.
  //    Te same bramki `widokKlienta` co w pasku komputera: przyciski operatora nigdy nie trafiają
  //    do klienta (pilnuje test_saas_studio_klient.py — każde wystąpienie etykiety).
  if (uklad.waski) {
    if (totalVisible === 0) return null;
    const pusto = count === 0;
    const podpis = pusto
      ? (uklad.dotyk ? 'Dotknij zdjęcia, żeby je zaznaczyć' : 'Kliknij zdjęcie, żeby je zaznaczyć')
      : `Zaznaczono ${count} z ${totalVisible}`;
    const pasek = {
      position:'absolute', left:0, right:0, bottom:0, zIndex:20,
      background:'var(--surface-1)', borderTop:'1px solid var(--line-2)',
      boxShadow:'var(--shadow)', paddingBottom:'env(safe-area-inset-bottom)',
    };
    if (uklad.telefon) {
      const zWiecej = (fn) => () => { onWiecej && onWiecej(false); fn && fn(); };
      // „Więcej” działa bez zaznaczenia tylko wtedy, gdy ma w sobie coś, co zaznaczenia nie wymaga.
      const wiecejBezZaznaczenia = widokKlienta && maPaczke;
      const duzy = (wylaczony, glowny) => ({
        minHeight:48, borderRadius:10, fontSize:15, fontWeight:600, fontFamily:'inherit',
        display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8, padding:'0 16px',
        cursor: wylaczony ? 'not-allowed' : 'pointer', opacity: wylaczony ? 0.45 : 1,
        background: glowny ? 'var(--accent)' : 'var(--panel-2)', color: glowny ? 'var(--on-accent)' : 'var(--text)',
        border: glowny ? 'none' : '1px solid var(--line-2)',
      });
      // Okno „Więcej” jest RODZEŃSTWEM paska, nie jego dzieckiem: dialog wewnątrz role="toolbar"
      // to złe zagnieżdżenie dla czytników ekranu (panel na diffie, 2026-09-30).
      return (
        <React.Fragment>
        <div ref={pasekRef} role="toolbar" aria-label="Zaznaczone zdjęcia" style={pasek}>
          <div style={{display:'flex', alignItems:'center', gap:8, padding:'4px 8px 0 16px', minHeight:CEL_DOTYKU}}>
            <span aria-live="polite" style={{flex:1, fontSize:13.5, color: pusto ? 'var(--text-2)' : 'var(--text)'}}>{podpis}</span>
            {!pusto && <BarBtn onClick={onDeselect} icon={<Icon.X size={12}/>} label="Odznacz"/>}
          </div>
          <div style={{display:'flex', gap:8, padding:'6px 12px 10px'}}>
            <button onClick={onWybielTlo} disabled={pusto} style={{...duzy(pusto, true), flex:1}}>
              <span style={{fontSize:13}}>🤍</span> Wybiel tło{pusto ? '' : ` (${count})`}
            </button>
            <button ref={wiecejRef} onClick={() => onWiecej && onWiecej(true)} disabled={pusto && !wiecejBezZaznaczenia}
              aria-haspopup="dialog" aria-expanded={wiecejOtwarte} style={duzy(pusto && !wiecejBezZaznaczenia, false)}>
              ⋯ Więcej
            </button>
          </div>
        </div>
          {wiecejOtwarte && (
            <OknoWysuwane strona="dol" etykieta="Więcej działań na zaznaczonych zdjęciach" onZamknij={() => onWiecej && onWiecej(false)} powrot={wiecejRef}>
              {/* O137: podmiana na oryginały z przeglądarki — klient ORAZ właściciel w chmurze (`drogaPhotoMatch`). */}
              {podmianaPrzegladarka && <>
                <PozycjaWiecej onClick={zWiecej(onPodmienOryginaly)} disabled={pusto} icon={<span style={{fontSize:13}}>📷</span>} label="Podmień na oryginały"/>
              </>}
              {widokKlienta && <>
                <PozycjaWiecej onClick={zWiecej(onPobierzDoObrobki)} disabled={pusto || paczkaBusy} icon={<Icon.Download size={14}/>} label={paczkaBusy ? 'Pakuję…' : 'Pobierz do obróbki'}/>
                {maPaczke && <PozycjaWiecej onClick={zWiecej(onWgrajGotowe)} disabled={paczkaBusy} icon={<Icon.Upload size={14}/>} label="Wgraj gotowe"/>}
              </>}
              {!widokKlienta && <>
                <PozycjaWiecej onClick={zWiecej(onEdit)} disabled={pusto} icon={<Icon.Edit size={14}/>} label="Edytuj zaznaczone"/>
                {!podmianaPrzegladarka && <PozycjaWiecej onClick={zWiecej(onPhotoMatch)} disabled={pusto} icon={<span style={{fontSize:13}}>🔗</span>} label="Photo Match"/>}
                <PozycjaWiecej onClick={zWiecej(onExport)} disabled={pusto} icon={<Icon.Download size={14}/>} label="Eksport"/>
                <PozycjaWiecej onClick={zWiecej(onOtworzFolderPhotoroom)} disabled={pusto || photoroomFolderBusy} icon={<Icon.Folder size={14}/>} label="Folder"/>
                <PozycjaWiecej onClick={zWiecej(onPhotoroomImport)} disabled={pusto} icon={<Icon.Upload size={14}/>} label="Podmień"/>
              </>}
              {/* U122 (decyzja operatora 2026-10-14, makieta 6): „Usuń” w obu widokach — do Kosza konta. */}
              <PozycjaWiecej onClick={zWiecej(onDelete)} disabled={pusto} danger icon={<Icon.Trash size={14}/>} label="Usuń"/>
              <PozycjaWiecej onClick={zWiecej(onDeselect)} disabled={pusto} icon={<Icon.X size={14}/>} label="Odznacz wszystko"/>
            </OknoWysuwane>
          )}
        </React.Fragment>
      );
    }
    // 768–1279 px: wszystkie działania w JEDNYM rzędzie, krótsze napisy (zmierzone: pełny podpis
    // i licznik wypychały „Wgraj gotowe” o 16–105 px poza ekran 768 px).
    const podpisKrotki = pusto ? (uklad.dotyk ? 'Dotknij zdjęcia' : 'Kliknij zdjęcie') : `${count} z ${totalVisible}`;
    // Pełny widok ma o dwa–trzy przyciski więcej niż klient i przy 768 px nie mieścił się w rzędzie („Podmień”
    // i „Usuń” za krawędzią okna, zmierzone 2026-10-01 i 2026-10-06) — zawija się do drugiego rzędu. Rząd klienta
    // zostaje jak był: jeden, przewijany.
    return (
      <div ref={pasekRef} role="toolbar" aria-label="Zaznaczone zdjęcia" style={pasek}>
        <div style={{display:'flex', alignItems:'center', gap:2, padding:'6px 10px', overflowX:'auto', ...(widokKlienta ? null : { flexWrap:'wrap', rowGap:4 })}}>
          <span aria-live="polite" aria-label={podpis} style={{flex:'0 0 auto', fontSize:12.5, color: pusto ? 'var(--text-2)' : 'var(--text)', paddingRight:10, marginRight:4, borderRight:'1px solid var(--line)'}}>{podpisKrotki}</span>
          {!widokKlienta && <>
            <BarBtn primary onClick={onEdit} disabled={pusto} icon={<Icon.Edit size={12}/>} label="Edytuj zaznaczone"/>
          </>}
          <BarBtn primary={widokKlienta} onClick={onWybielTlo} disabled={pusto} icon={<span style={{fontSize:11}}>🤍</span>} label="Wybiel tło"/>
          {!widokKlienta && <>
            {!podmianaPrzegladarka && <BarBtn onClick={onPhotoMatch} disabled={pusto} icon={<span style={{fontSize:11}}>🔗</span>} label="Photo Match"/>}
          </>}
          {podmianaPrzegladarka && <>
            <BarBtn onClick={onPodmienOryginaly} disabled={pusto} icon={<span style={{fontSize:11}}>📷</span>} label="Oryginały"/>
          </>}
          <BarBtn onClick={onDeselect} disabled={pusto} icon={<Icon.X size={12}/>} label="Odznacz"/>
          {/* B119 (raport B117 pkt 6, makieta 6): „Eksport” = paczka do Photoroom w folderze + Finder na maszynie
              serwera — narzędzie operatora. Klient ma swoją jedną drogę „Pobierz” (do obróbki) niżej. */}
          {!widokKlienta && <>
            <BarBtn onClick={onExport} disabled={pusto} icon={<Icon.Download size={12}/>} label="Eksport"/>
            <BarBtn onClick={onOtworzFolderPhotoroom} disabled={pusto || photoroomFolderBusy} icon={<Icon.Folder size={12}/>} label="Folder"/>
            <BarBtn onClick={onPhotoroomImport} disabled={pusto} icon={<Icon.Upload size={12}/>} label="Podmień"/>
          </>}
          {widokKlienta && <>
            <BarBtn onClick={onPobierzDoObrobki} disabled={pusto || paczkaBusy} icon={<Icon.Download size={12}/>} label={paczkaBusy ? 'Pakuję…' : 'Pobierz'}/>
            {maPaczke && <BarBtn onClick={onWgrajGotowe} disabled={paczkaBusy} icon={<Icon.Upload size={12}/>} label="Wgraj gotowe"/>}
          </>}
          <BarBtn danger onClick={onDelete} disabled={pusto} icon={<Icon.Trash size={12}/>} label="Usuń"/>
        </div>
      </div>
    );
  }
  const visible = count > 0;
  return (
    <div ref={pasekRef} style={{
      position:'absolute',
      // Pełny widok: wyśrodkowana wyspa, która NIE wychodzi poza obszar zdjęć — gdy przyciski się nie mieszczą
      // (1280 px: wyspa miała 1054 px w obszarze 1020 px, „Usuń” za krawędzią okna), zawija się do drugiego rzędu.
      // Widok klienta zostaje co do piksela jak był (mieści się; zmierzone `porownaj.py` 2026-10-07 — samo
      // przejście na szerokość z treści przesuwało jego przyciski o 5 px).
      ...(widokKlienta
        ? { left:'50%', transform:'translateX(-50%)' }
        : { left:12, right:12, margin:'0 auto', width:'fit-content', maxWidth:'calc(100% - 24px)', boxSizing:'border-box', flexWrap:'wrap', rowGap:4 }),
      bottom: visible ? 18 : -160,   // schowana: poniżej krawędzi także wtedy, gdy ma dwa rzędy
      transition:'bottom 0.22s cubic-bezier(0.2, 0.8, 0.3, 1)',
      // P4: nieprzezroczysta powierzchnia z tokenów (rozmycie pod półprzezroczystym ciemnym tłem znika —
      // nad siatką zdjęć dawało w jasnym motywie ciemną wyspę z jasnym napisem).
      background:'var(--surface-1)',
      border:'1px solid var(--line-2)', borderRadius:12,
      padding:'8px 8px 8px 14px',
      display:'flex', alignItems:'center', gap:4,
      boxShadow:'var(--shadow)',
      zIndex:20,
    }}>
      <div style={{display:'flex', alignItems:'center', gap:8, paddingRight:10, borderRight:'1px solid var(--line)', marginRight:4}}>
        <div style={{width:22, height:22, borderRadius:6, background:'var(--accent)', color:'var(--on-accent)', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontVariantNumeric:'tabular-nums'}}>{count}</div>
        <span style={{fontSize:12.5, fontWeight:500}}>zaznaczono</span>
        <span style={{fontSize:11, color:'var(--text-3)'}}>z {totalVisible}</span>
      </div>
      {/* LF-SAAS Studio-klient (2026-09-07, decyzja operatora): Edytor Studia, Photo Match
          i kasowanie ze Studia są NARZĘDZIAMI OPERATORA — u klienta chmury ZNIKAJĄ (nie „wkrótce",
          zasada 08-03 „zostaje jedna opcja → usuń wybór"). Powód zmierzony (O-49): Edytor zapisuje
          na ulotny dysk kontenera, a lista czyta R2 — wynik znikałby natychmiast; Photo Match pyta
          folder oryginałów na dysku serwera. Serwer i tak odmawia (`lf_studio_guard`, 404) —
          ten warunek chowa tylko przycisk. Pilnuje: `tests/test_saas_studio_klient.py`. */}
      {!widokKlienta && <>
        <BarBtn primary onClick={onEdit} icon={<Icon.Edit size={12}/>} label="Edytuj zaznaczone"/>
      </>}
      {/* LF-WYBIEL-Z-GALERII (2026-08-05): przejście na ekran „Wybiel tło" z zaznaczonymi
          ZDJĘCIAMI. Sam przycisk niczego nie uruchamia i nic nie kosztuje — prowadzi na
          listę, gdzie widać stan każdego zdjęcia i cenę PRZED kliknięciem (decyzja operatora:
          „płatne tylko przez ekran, nigdy na ślepo").
          BEZ `lvl-full` / bez warunku `widokKlienta`: wybielanie tła to funkcja produktu,
          a nie maszynownia — klient ma ją widzieć (decyzja operatora 2026-08-05). Etykieta
          nie niesie nazwy dostawcy ani stawki, więc jest ta sama dla obu widoków. */}
      <BarBtn onClick={onWybielTlo} icon={<span style={{fontSize:11}}>🤍</span>} label="Wybiel tło"/>
      {!widokKlienta && <>
        {!podmianaPrzegladarka && <BarBtn onClick={onPhotoMatch} icon={<span style={{fontSize:11}}>🔗</span>} label="Photo Match"/>}
      </>}
      {/* Photo Match PRZEZ PRZEGLĄDARKĘ (2026-09-07, plan SAAS/PLAN_photo_match_przegladarka.md):
          następca Photo Matcha dla klienta chmury — oryginały leżą w JEGO przeglądarce (folder
          z telefonu), nie na dysku serwera. Serwer dostaje miniaturki → odciski → tylko dopasowane,
          zmniejszone zdjęcia; podmiana przez magazyn. Trasy otwarte bramką `_STUDIO_KLIENT_ALLOW` (3).
          O137 (2026-10-20, decyzja operatora 2026-10-19 15:09): także WŁAŚCICIEL w chmurze — tam stary
          Photo Match wyżej jest martwy (dysk serwera = 404), więc znika; operator na Macu bez zmian. */}
      {podmianaPrzegladarka && <>
        <BarBtn onClick={onPodmienOryginaly} icon={<span style={{fontSize:11}}>📷</span>} label="Podmień na oryginały"/>
      </>}
      <BarBtn onClick={onDeselect} icon={<Icon.X size={12}/>} label="Odznacz" hint="Esc"/>
      {/* LF-PHOTOROOM-EDYTOR: ogniwo między wysyłką a podmianą — otwiera folder na pliki
          pobrane z Photoroom (to samo dodane w Edytorze tego samego dnia).
          LF-SAAS-B1 (2026-08-05): u klienta znika razem z pływającą parą tych samych
          przycisków niżej — otwiera Finder na maszynie serwera (patrz nota tam).
          B119 (raport B117 pkt 6, makieta 6): „Eksport” (paczka do Photoroom + Finder) dołącza do tej grupy —
          klient ma jedną drogę „Pobierz do obróbki” niżej (kolejność operatora R11.10 bez zmian). */}
      {!widokKlienta && <>
        <BarBtn onClick={onExport} icon={<Icon.Download size={12}/>} label="Eksport"/>
        <BarBtn onClick={onOtworzFolderPhotoroom} disabled={photoroomFolderBusy} icon={<Icon.Folder size={12}/>} label="Folder"/>
        <BarBtn onClick={onPhotoroomImport} icon={<Icon.Upload size={12}/>} label="Podmień"/>
      </>}
      {/* B2 (2026-08-05): ta sama droga u KLIENTA — obróbka u zewnętrznego dostawcy, tylko
          pliki wędrują przez przeglądarkę zamiast przez folder na dysku. Dwa przyciski,
          bo to dwa osobne momenty: zdjęcia wychodzą, a wynik wraca godzinę później.
          Etykiety mówią, co się STANIE (pobierze się plik / wgra się plik), a nie
          „Folder"/„Podmień" — u klienta nie ma żadnego folderu do otwarcia.
          BEZ nazwy dostawcy: klient wybiera narzędzie sam (know-how policy). */}
      {widokKlienta && <>
        <BarBtn onClick={onPobierzDoObrobki} disabled={paczkaBusy} icon={<Icon.Download size={12}/>} label={paczkaBusy ? 'Pakuję…' : 'Pobierz do obróbki'}/>
        {maPaczke && <BarBtn onClick={onWgrajGotowe} disabled={paczkaBusy} icon={<Icon.Upload size={12}/>} label="Wgraj gotowe"/>}
      </>}
      {/* U122 (decyzja operatora 2026-10-14 „klient w SaaS online ma mieć taką samą funkcję, ogólnie funkcje
          usuwania”, makieta 6): „Usuń” w obu widokach. Trasy przez magazyn i z izolacją konta — `delete-photos`
          od Z19 a (2026-10-03), `DELETE /api/listings` z testem drugiego konta U122; wszystko idzie do Kosza konta. */}
      <BarBtn danger onClick={onDelete} icon={<Icon.Trash size={12}/>} label="Usuń" hint="⌫"/>
    </div>
  );
}

/* ---------- TOAST ---------- */

function Toast({ toast }) {
  const color = toast.kind === 'danger' ? 'var(--danger)' : toast.kind === 'ok' ? 'var(--ok)' : 'var(--accent)';
  return (
    <div style={{
      position:'absolute', top:16, left:'50%', transform:'translateX(-50%)',
      background:'var(--panel-2)', color:'var(--text)',
      border:'1px solid var(--line-2)',
      borderLeft: `3px solid ${color}`,
      padding:'9px 14px', borderRadius:8, fontSize:12.5,
      boxShadow:'var(--shadow)', zIndex:30,
      animation:'toastIn 0.2s cubic-bezier(0.2,0.8,0.3,1)',
    }}>{toast.msg}</div>
  );
}

function KeyboardHints({ widokKlienta = false }) {
  const kbd = {background:'var(--panel-2)', border:'1px solid var(--line)', padding:'1px 5px', borderRadius:3, color:'var(--text-2)'};
  // Z32 (tura 90 Ł, łowca Ł9): u klienta skróty po polsku i BEZ „⌫ usuń” — klawisz celowo nic tam nie robi
  // (kasowanie = dysk serwera, `onKeyDown` niżej). Maszynownia bez zmian.
  if (widokKlienta) return (
    <div style={{position:'absolute', right:16, bottom:14, fontSize:10.5, color:'var(--text-3)', display:'flex', gap:10, pointerEvents:'none', zIndex:5}}>
      <span><kbd style={kbd}>⌘A</kbd> zaznacz wszystko</span>
      <span><kbd style={kbd}>⌘⇧A</kbd> odwróć</span>
      <span><kbd style={kbd}>⌘ + klik</kbd> dołóż zdjęcie</span>
      <span><kbd style={kbd}>przeciągnij</kbd> zaznacz obszar</span>
      <span><kbd style={kbd}>⌘.</kbd> odznacz</span>
    </div>
  );
  return (
    <div style={{position:'absolute', right:16, bottom:14, fontSize:10.5, color:'var(--text-3)', display:'flex', gap:10, fontFamily:'JetBrains Mono, monospace', pointerEvents:'none', zIndex:5}}>
      <span><kbd style={kbd}>⌘A</kbd> zaznacz</span>
      <span><kbd style={kbd}>⌘⇧A</kbd> odwróć</span>
      <span><kbd style={kbd}>⌘click</kbd> multi</span>
      <span><kbd style={kbd}>drag</kbd> lasso</span>
      <span><kbd style={kbd}>⌘.</kbd> odznacz</span>
      <span><kbd style={kbd}>⌫</kbd> usuń</span>
    </div>
  );
}

function EmptyState({ onReset, msg }) {
  return (
    <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60%', color:'var(--text-3)', textAlign:'center', gap:8}}>
      <div style={{fontSize:40, opacity:0.4}}>⌘</div>
      <div style={{fontSize:14, color:'var(--text-2)', fontWeight:500}}>{msg || 'Brak zdjęć pasujących do filtrów'}</div>
      <div style={{fontSize:12}}>Zmień filtry albo wyczyść wyszukiwanie</div>
      {onReset && <button onClick={onReset} style={{marginTop:10, background:'var(--panel)', border:'1px solid var(--line)', color:'var(--text)', padding:'6px 12px', borderRadius:6, cursor:'pointer', fontFamily:'inherit', fontSize:12}}>Wyczyść filtry</button>}
    </div>
  );
}

/* ---------- PHOTO MATCH V2 MODAL (Sesja 22U, 2026-05-04) ---------- */

/* Modal w Galerii — operator zaznacza zdjecia z N ogloszen, klika "🔗 Photo Match"
   w SelectionBar. Modal grupuje wyniki per source_id, leci match per batch (multi-source
   endpoint /api/photo-match/match z source_ids: [...]). Po replace backend usuwa
   garment.png dla podmienionych plikow — przy nastepnym wejsciu do Edycji ensure-masks
   wymusi regeneracje maski na ORG (lepsza jakosc). */

const pmBtnStyle = (busy) => ({
  background: busy ? 'var(--panel-2)' : 'var(--accent)',
  color: busy ? 'var(--text-3)' : 'var(--on-accent)',
  border: 'none', padding: '8px 16px', borderRadius: 7,
  fontSize: 14, fontWeight: 600,
  cursor: busy ? 'wait' : 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
});

const pmSmallBtn = (variant) => ({
  background: 'transparent',
  border: `1px solid ${variant === 'danger' ? 'var(--danger)' : 'var(--line-2)'}`,
  color: variant === 'danger' ? 'var(--danger-text)' : 'var(--text-2)',
  padding: '4px 8px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
});

async function pmReadError(resp, fallback) {
  const raw = await resp.text().catch(() => '');
  let msg = fallback;
  if (raw && raw.trim()) {
    try {
      const data = JSON.parse(raw);
      msg = data && data.detail;
      if (Array.isArray(msg)) {
        const lines = msg.map(d => (d && typeof d === 'object' && d.msg) ? d.msg : null).filter(Boolean);
        msg = lines.length > 0 ? lines.join(' • ') : fallback;
      }
      if (typeof msg !== 'string' || !msg.trim()) msg = fallback;
    } catch {
      msg = raw.trim();
    }
  }
  return `${msg} (HTTP ${resp.status})`;
}

function PhotoMatchModal({ selectedPhotoRefs, onClose, fire, onReplaceComplete }) {
  // selectedPhotoRefs: [{source_id, photo_index}] z zaznaczenia w Galerii
  const sourceIds = useMemo(() => {
    const set = new Set();
    for (const r of selectedPhotoRefs) set.add(r.source_id);
    return Array.from(set).sort();
  }, [selectedPhotoRefs]);

  const [stage, setStage] = useState('loading');
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [indexProgress, setIndexProgress] = useState({ current: 0, total: 0, filename: '' });
  const [matchResult, setMatchResult] = useState(null);  // {groups: [...]}
  const [decisions, setDecisions] = useState({});  // `${source_id}__${webp_filename}` -> 'accept'|'skip'
  const [editsAccepted, setEditsAccepted] = useState({});
  const [replaceResult, setReplaceResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    fetch('/api/photo-match/accounts')
      .then(r => r.json())
      .then(data => {
        setAccounts(data.accounts || []);
        if ((data.accounts || []).length === 1) setAccount(data.accounts[0].name);
        setStage('pickAccount');
      })
      .catch(e => { setErrorMsg(`Brak listy kont: ${e.message}`); setStage('error'); });
  }, []);

  const runMatch = useCallback((acc) => {
    setStage('matching');
    fetch('/api/photo-match/match', {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ source_ids: sourceIds, account: acc, source: 'studio' }),
    }).then(async r => {
      if (r.ok) return r.json();
      const msg = await pmReadError(r, 'Match failed');
      throw new Error(`${msg} (${sourceIds.length} ogloszen)`);
    })
      .then(data => {
        setMatchResult(data);
        const initialDec = {};
        const initialEdits = {};
        for (const g of (data.groups || [])) {
          for (const m of (g.matches || [])) {
            const key = `${g.source_id}__${m.webp_filename}`;
            initialDec[key] = m.matched ? 'accept' : 'skip';
            const e = (g.edits || {})[m.webp_filename];
            if (e && (e.bg || e.shadow || e.vinted_dedupe)) initialEdits[key] = false;
          }
        }
        setDecisions(initialDec);
        setEditsAccepted(initialEdits);
        setStage('results');
      })
      .catch(e => { setErrorMsg(e.message); setStage('error'); });
  }, [sourceIds]);

  const startIndexing = useCallback((acc) => {
    setStage('indexing');
    setIndexProgress({ current: 0, total: 0, filename: '' });
    fetch('/api/photo-match/index', {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ account: acc }),
    }).then(async resp => {
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // 22AZ: split na \r?\n\r?\n (HTTP CRLF + LF) zamiast tylko \n\n — defense
        // gdy proxy/load balancer znormalizuje line endings. Dual review P3 (Gemini Flash 22AY).
        const lines = buf.split(/\r?\n\r?\n/);
        buf = lines.pop() || '';
        for (const line of lines) {
          const m = line.match(/^data:\s*(.+)$/m);
          if (!m) continue;
          try {
            const ev = JSON.parse(m[1]);
            if (ev.type === 'progress') setIndexProgress({ current: ev.current, total: ev.total, filename: ev.filename });
            else if (ev.type === 'done') { runMatch(acc); return; }
            else if (ev.type === 'error') { setErrorMsg(ev.message); setStage('error'); return; }
          } catch {}
        }
      }
    }).catch(e => { setErrorMsg(`Indeksowanie: ${e.message}`); setStage('error'); });
  }, [runMatch]);

  const handleAccountConfirm = useCallback(() => {
    if (!account) return;
    const acct = accounts.find(a => a.name === account);
    if (acct && acct.indexed_count > 0) runMatch(account);
    else startIndexing(account);
  }, [account, accounts, startIndexing, runMatch]);

  const handleApply = useCallback(() => {
    if (!matchResult) return;
    const blocked = [];
    const accepted = [];
    for (const g of matchResult.groups || []) {
      const proposalsByFilename = {};
      for (const p of (g.sequence_proposals || [])) {
        proposalsByFilename[p.webp_filename] = p;
      }
      for (const m of g.matches || []) {
        const key = `${g.source_id}__${m.webp_filename}`;
        if (decisions[key] !== 'accept') continue;
        // Pewny match z Hungarian albo propozycja sekwencyjna (R13, sesja 22V)
        const proposal = proposalsByFilename[m.webp_filename];
        if (!m.matched && !proposal) continue;
        const e = (g.edits || {})[m.webp_filename];
        if (e && (e.bg || e.shadow || e.vinted_dedupe) && !editsAccepted[key]) {
          blocked.push(`${g.source_id}/${m.webp_filename}`);
          continue;
        }
        const orgPath = m.matched ? m.org_path : proposal.suggested_org_path;
        accepted.push({ source_id: g.source_id, webp_filename: m.webp_filename, org_path: orgPath });
      }
    }
    if (blocked.length > 0) {
      alert(`Zaznacz "Zaakceptuj utratę edycji" dla:\n${blocked.join('\n')}`);
      return;
    }
    if (accepted.length === 0) {
      fire && fire('Nic nie zaakceptowano', 'danger');
      return;
    }
    setStage('replacing');
    fetch('/api/photo-match/replace', {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ accepted, source: 'studio' }),
    }).then(async r => {
      if (r.ok) return r.json();
      throw new Error(await pmReadError(r, 'Replace failed'));
    })
      .then(data => {
        setReplaceResult(data);
        setStage('done');
        fire && fire(`Podmieniono ${data.replaced}/${data.total} zdjęć (maski zresetowane: ${data.cleared_garment_count})`, 'ok');
        // 22X bug fix: powiadom Galerię żeby zrobiła refetch (cache-bust thumby + clear browser cache)
        onReplaceComplete && onReplaceComplete(data);
      })
      .catch(e => { setErrorMsg(e.message); setStage('error'); });
  }, [matchResult, decisions, editsAccepted, fire, onReplaceComplete]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 250,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--panel)', border: '1px solid var(--line-2)',
        borderRadius: 12, width: '100%', maxWidth: 1000, maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: 'var(--shadow)',
      }}>
        <header style={{
          padding: '16px 20px', borderBottom: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{fontSize: 20}}>🔗</span>
          <div style={{flex: 1}}>
            <div style={{fontSize: 16, fontWeight: 600}}>Photo Match V2</div>
            <div style={{fontSize: 12, color: 'var(--text-3)'}}>
              Podmień Vinted skompresowane WebP na oryginały z telefonu · {sourceIds.length} ogłoszeń · {selectedPhotoRefs.length} zdjęć zaznaczonych
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid var(--line-2)',
            color: 'var(--text-2)', padding: '6px 12px', borderRadius: 6,
            fontSize: 13, cursor: 'pointer',
          }}>Zamknij</button>
        </header>

        <div style={{flex: 1, overflowY: 'auto', padding: 20}}>
          {stage === 'loading' && <div style={{textAlign: 'center', padding: 40, color: 'var(--text-3)'}}>Ładowanie…</div>}

          {stage === 'pickAccount' && (
            <div>
              <div style={{fontSize: 14, marginBottom: 12, color: 'var(--text-2)'}}>
                Wybierz konto (folder w <code>~/Documents/ListForge-data/originals/</code>):
              </div>
              {accounts.length === 0 ? (
                <div style={{padding: 16, background: 'var(--panel-2)', borderRadius: 8, color: 'var(--text-3)'}}>
                  Brak kont. Utwórz folder w <code>originals/</code> i wgraj zdjęcia z telefonu (JPEG).
                </div>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  {accounts.map(a => (
                    <label key={a.name} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', background: account === a.name ? 'var(--panel-2)' : 'transparent',
                      border: `1px solid ${account === a.name ? 'var(--accent)' : 'var(--line)'}`,
                      borderRadius: 8, cursor: 'pointer',
                    }}>
                      <input type="radio" name="pm-account" checked={account === a.name}
                        onChange={() => setAccount(a.name)} style={{accentColor: 'var(--accent)'}}/>
                      <span style={{fontWeight: 500}}>{a.name}</span>
                      <span style={{flex: 1}}/>
                      <span style={{fontSize: 12, color: 'var(--text-3)'}}>
                        {a.indexed_count > 0 ? `${a.indexed_count} zdjęć w indeksie` : 'nieindeksowane'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <div style={{marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                <button onClick={handleAccountConfirm} disabled={!account} style={pmBtnStyle(!account)}>
                  {account && (accounts.find(a => a.name === account)?.indexed_count > 0)
                    ? `Dopasuj ${sourceIds.length} ogłoszeń` : 'Indeksuj i dopasuj'}
                </button>
              </div>
            </div>
          )}

          {stage === 'indexing' && (
            <div>
              <div style={{fontSize: 14, marginBottom: 14, color: 'var(--text-2)'}}>
                Indeksowanie konta <strong>{account}</strong>… (perceptual hash 256-bit per zdjęcie)
              </div>
              <div style={{height: 8, background: 'var(--panel-2)', borderRadius: 4, overflow: 'hidden', marginBottom: 8}}>
                <div style={{
                  height: '100%',
                  width: indexProgress.total > 0 ? `${(indexProgress.current / indexProgress.total) * 100}%` : '0%',
                  background: 'var(--accent)', transition: 'width 200ms',
                }}/>
              </div>
              <div style={{fontSize: 12, color: 'var(--text-3)', fontFamily: 'JetBrains Mono, monospace'}}>
                {indexProgress.current} / {indexProgress.total} · {indexProgress.filename}
              </div>
            </div>
          )}

          {stage === 'matching' && (
            <div style={{textAlign: 'center', padding: 40, color: 'var(--text-3)'}}>
              Dopasowywanie {sourceIds.length} ogłoszeń (Hungarian assignment)…
            </div>
          )}

          {stage === 'results' && matchResult && (
            <PhotoMatchMultiResults
              account={account}
              matchResult={matchResult}
              selectedPhotoRefs={selectedPhotoRefs}
              decisions={decisions}
              setDecisions={setDecisions}
              editsAccepted={editsAccepted}
              setEditsAccepted={setEditsAccepted}
            />
          )}

          {stage === 'replacing' && (
            <div style={{textAlign: 'center', padding: 40, color: 'var(--text-3)'}}>
              Atomic conversion WebP → JPEG Q95… (ICC profile + EXIF transpose + img.verify + reset garment.png)
            </div>
          )}

          {stage === 'done' && replaceResult && (
            <div style={{padding: 14, background: 'var(--panel-2)', border: '1px solid var(--ok)', borderRadius: 8}}>
              <div style={{fontSize: 15, fontWeight: 600, marginBottom: 6}}>✓ Podmieniono {replaceResult.replaced} z {replaceResult.total} zdjęć</div>
              <div style={{fontSize: 13, color: 'var(--text-2)'}}>
                Backupy: {(replaceResult.groups || []).length} folderów · maski zresetowane: {replaceResult.cleared_garment_count}
              </div>
              <div style={{fontSize: 12, color: 'var(--text-3)', marginTop: 8}}>
                Wchodząc do Edycji zaznaczonych zdjęć dostaniesz pełną jakość ORG + automatycznie wygenerowaną maskę alpha (Apple Vision).
              </div>
              {replaceResult.warnings?.length > 0 && (
                <div style={{marginTop: 8, fontSize: 12, color: 'var(--warn-text)'}}>
                  Ostrzeżenia: {replaceResult.warnings.join('; ')}
                </div>
              )}
            </div>
          )}

          {stage === 'error' && (
            <div style={{padding: 14, background: 'var(--panel-2)', border: '1px solid var(--danger)', borderRadius: 8}}>
              <div style={{fontSize: 14, fontWeight: 600, marginBottom: 6, color: 'var(--danger-text)'}}>Błąd</div>
              <div style={{fontSize: 13, color: 'var(--text-2)'}}>{errorMsg}</div>
            </div>
          )}
        </div>

        {(stage === 'results' || stage === 'done' || stage === 'error') && (
          <footer style={{
            padding: '14px 20px', borderTop: '1px solid var(--line)',
            display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          }}>
            {stage === 'results' && (
              <>
                <div style={{flex: 1, fontSize: 13, color: 'var(--text-3)'}}>
                  <button onClick={() => startIndexing(account)} style={pmSmallBtn()}>Aktualizuj indeks</button>
                </div>
                <button onClick={onClose} style={{
                  background: 'transparent', border: '1px solid var(--line-2)',
                  color: 'var(--text)', padding: '8px 14px', borderRadius: 7,
                  fontSize: 14, cursor: 'pointer',
                }}>Anuluj</button>
                <button onClick={handleApply} style={pmBtnStyle(false)}>Zastosuj podmianę</button>
              </>
            )}
            {(stage === 'done' || stage === 'error') && (
              <button onClick={onClose} style={pmBtnStyle(false)}>Zamknij</button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function PhotoMatchMultiResults({ account, matchResult, selectedPhotoRefs, decisions, setDecisions, editsAccepted, setEditsAccepted }) {
  // Zdjecia zaznaczone w Galerii: filtr per group/photo do tylko tych ktore operator zaznaczyl
  const selectedKey = useMemo(() => {
    const s = new Set();
    for (const r of selectedPhotoRefs) s.add(`${r.source_id}__${r.photo_index}`);
    return s;
  }, [selectedPhotoRefs]);

  const summary = `${matchResult.matched_high} pewnych · ${matchResult.matched_medium} możliwych · ${matchResult.no_match} bez ORG`;

  return (
    <div>
      <div style={{
        padding: 12, background: 'var(--panel-2)', borderRadius: 8, marginBottom: 14,
        fontSize: 13, color: 'var(--text-2)',
      }}>
        Konto <strong>{account}</strong> ({matchResult.total_org} ORG) → {matchResult.total_groups} ogłoszeń, {matchResult.total_webp} zdjęć.
        Wynik: <strong>{summary}</strong>.
        {matchResult.already_matched_count > 0 && (
          <span style={{color: 'var(--text-3)'}}> · {matchResult.already_matched_count} już zmatchowanych</span>
        )}
      </div>

      {(matchResult.groups || []).map(g => {
        if (g.error) {
          return (
            <div key={g.source_id} style={{
              padding: 10, marginBottom: 10, background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
              border: '1px solid var(--danger)', borderRadius: 8, fontSize: 12, color: 'var(--text-2)',
            }}>
              <strong>{g.source_id}</strong>: błąd — {g.error}
            </div>
          );
        }
        // Filter matches do tylko zaznaczonych w Galerii (jesli operator zaznaczyl podzbior batcha)
        const filtered = (g.matches || []).filter(m => selectedKey.has(`${g.source_id}__${m.webp_index}`));
        const showMatches = filtered.length > 0 ? filtered : g.matches;

        return (
          <div key={g.source_id} style={{marginBottom: 18}}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              paddingBottom: 8, marginBottom: 10, borderBottom: '1px solid var(--line)',
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: 2, background: colorFromString(g.source_id),
              }}/>
              <code style={{fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 600}}>{g.source_id}</code>
              <span style={{fontSize: 12, color: 'var(--text-3)'}}>
                {g.matched_high} pewnych · {g.matched_medium} możliwych · {g.no_match} bez ORG
                {filtered.length > 0 && filtered.length < g.matches.length && (
                  <span> · pokazuję {filtered.length}/{g.matches.length} (zaznaczone w Studiu)</span>
                )}
              </span>
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
              {showMatches.map(m => {
                const key = `${g.source_id}__${m.webp_filename}`;
                const decision = decisions[key] || 'skip';
                const edits = (g.edits || {})[m.webp_filename];
                const hasPhotoEdits = edits && (edits.bg || edits.shadow || edits.vinted_dedupe);
                const editsLabel = edits ? [
                  edits.bg && 'Tło', edits.shadow && 'Cień', edits.vinted_dedupe && 'Vinted Dedupe'
                ].filter(Boolean).join(', ') : '';

                // Sequence proposal lookup (R13, sesja 22V): dla confidence='none' sprawdz czy
                // backend zaproponowal kontynuacje sekwencji IMG_NNNN.
                const proposal = !m.matched
                  ? (g.sequence_proposals || []).find(p => p.webp_filename === m.webp_filename)
                  : null;
                const hasProposal = !!proposal;

                // P4 (nowy wygląd): kolory pewności na tokenach z kontrastem ≥ 4,5:1 w OBU motywach.
                // Pomarańcz (MOŻLIWE + PROPOZYCJA, R13) = zmienna Studia --propozycja, NIGDY --accent.
                const confColor = m.confidence === 'high' ? 'var(--green-text)' :
                                  m.confidence === 'medium' ? 'var(--propozycja)' :
                                  hasProposal ? 'var(--propozycja)' : 'var(--muted)';
                let confLabel;
                if (m.confidence === 'high') confLabel = `PEWNE (dist=${m.distance})`;
                else if (m.confidence === 'medium') confLabel = `MOŻLIWE (dist=${m.distance}${m.used_dhash ? ', dHash' : ''})`;
                else if (hasProposal) {
                  const tag = proposal.confidence === 'high' ? 'PROPOZYCJA'
                            : proposal.confidence === 'medium' ? 'PROPOZYCJA?'
                            : 'MOŻE';
                  confLabel = `${tag} (sekwencja)`;
                } else confLabel = 'BRAK ORG';

                const showAcceptable = m.matched || hasProposal;

                return (
                  <div key={m.webp_filename} style={{
                    display: 'flex', gap: 10, padding: 10,
                    background: decision === 'accept' ? 'var(--panel-2)' : 'transparent',
                    border: `1px solid ${decision === 'accept' ? (hasProposal && !m.matched ? 'var(--propozycja)' : 'var(--accent)') : 'var(--line)'}`,
                    borderRadius: 8,
                  }}>
                    <div style={{width: 90, height: 120, background: 'var(--pod-podgladem)', color: 'var(--na-zdjeciu-napis)', borderRadius: 6, overflow: 'hidden', flexShrink: 0}}>
                      <img src={`/api/thumb/${g.source_id}/${encodeURIComponent(m.webp_filename)}?w=180`}
                        alt={m.webp_filename}
                        style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
                    </div>
                    <div style={{
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 10.5, color: confColor, fontWeight: 600, gap: 4, minWidth: 80,
                    }}>
                      <div style={{fontSize: 16, color: 'var(--text-3)'}}>→</div>
                      <div style={{textAlign: 'center'}}>{confLabel}</div>
                    </div>
                    <div style={{width: 90, height: 120, background: 'var(--pod-podgladem)', color: 'var(--na-zdjeciu-napis)', borderRadius: 6, overflow: 'hidden', flexShrink: 0}}>
                      {m.matched ? (
                        <img src={`/api/photo-match/org-thumb?account=${encodeURIComponent(account)}&filename=${encodeURIComponent(m.org_filename)}&w=180${m.org_subfolder ? `&subfolder=${encodeURIComponent(m.org_subfolder)}` : ''}`}
                          alt={m.org_filename}
                          style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
                      ) : hasProposal ? (
                        <img src={`/api/photo-match/org-thumb?account=${encodeURIComponent(account)}&filename=${encodeURIComponent(proposal.suggested_org_filename)}&w=180${proposal.suggested_subfolder ? `&subfolder=${encodeURIComponent(proposal.suggested_subfolder)}` : ''}`}
                          alt={proposal.suggested_org_filename}
                          style={{width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85}}/>
                      ) : (
                        <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, color: 'var(--na-zdjeciu-napis)', textAlign: 'center', padding: 6}}>BRAK ORG</div>
                      )}
                    </div>
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12}}>
                      <div style={{fontWeight: 500, color: 'var(--text)', fontSize: 12.5}}>{m.webp_filename}</div>
                      {m.matched && (
                        <div style={{color: 'var(--text-3)', fontSize: 11.5}}>
                          ORG: <code style={{fontFamily: 'JetBrains Mono, monospace'}}>{m.org_subfolder ? `${m.org_subfolder}/` : ''}{m.org_filename}</code>
                          {' '}({m.org_width}×{m.org_height})
                        </div>
                      )}
                      {hasProposal && !m.matched && (
                        <div style={{color: 'var(--text-3)', fontSize: 11.5}}>
                          Propozycja: <code style={{fontFamily: 'JetBrains Mono, monospace'}}>{proposal.suggested_org_filename}</code>
                          {' '}({proposal.suggested_org_width}×{proposal.suggested_org_height})
                          <div style={{fontSize: 10.5, color: 'var(--text-3)', marginTop: 2, fontStyle: 'italic'}}>
                            {proposal.reason}
                          </div>
                        </div>
                      )}
                      {hasPhotoEdits && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          // R5 „żółty box” (edycje zostaną zresetowane) — ostrzeżenie, więc --warn-text
                          // (ten sam kolor co zapas --orange w wyglad.css), tło ≤ 8 % (plan §4a).
                          padding: '5px 8px', background: 'color-mix(in srgb, var(--warn-text) 8%, transparent)',
                          border: '1px solid var(--warn-text)', borderRadius: 5,
                          fontSize: 11, color: 'var(--text-2)',
                        }}>
                          <span>⚠️ <strong>{editsLabel}</strong> zostaną zresetowane</span>
                          <span style={{flex: 1}}/>
                          <label style={{display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer'}}>
                            <input type="checkbox"
                              checked={!!editsAccepted[key]}
                              onChange={e => setEditsAccepted(prev => ({...prev, [key]: e.target.checked}))}
                              style={{accentColor: 'var(--warn-text)'}}/>
                            Zaakceptuj
                          </label>
                        </div>
                      )}
                      <div style={{flex: 1}}/>
                      {showAcceptable ? (
                        <div style={{display: 'flex', gap: 6}}>
                          <label style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '4px 9px', borderRadius: 5, cursor: 'pointer',
                            background: decision === 'accept' ? (hasProposal && !m.matched ? 'color-mix(in srgb, var(--propozycja) 8%, transparent)' : 'color-mix(in srgb, var(--green) 8%, transparent)') : 'transparent',
                            border: `1px solid ${decision === 'accept' ? (hasProposal && !m.matched ? 'var(--propozycja)' : 'var(--green)') : 'var(--line-2)'}`,
                            fontSize: 11.5,
                          }}>
                            <input type="radio" name={`dec-${key}`} checked={decision === 'accept'}
                              onChange={() => setDecisions(prev => ({...prev, [key]: 'accept'}))}
                              style={{accentColor: hasProposal && !m.matched ? 'var(--propozycja)' : 'var(--green)'}}/>
                            Podmień
                          </label>
                          <label style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '4px 9px', borderRadius: 5, cursor: 'pointer',
                            background: decision === 'skip' ? 'var(--surface-3)' : 'transparent',
                            border: `1px solid ${decision === 'skip' ? 'var(--faint)' : 'var(--line-2)'}`,
                            fontSize: 11.5,
                          }}>
                            <input type="radio" name={`dec-${key}`} checked={decision === 'skip'}
                              onChange={() => setDecisions(prev => ({...prev, [key]: 'skip'}))}/>
                            Pomiń
                          </label>
                        </div>
                      ) : (
                        <div style={{fontSize: 11, color: 'var(--text-3)'}}>Brak dopasowania → zostaje WebP</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- APP ---------- */

function App() {
  // LF-SAAS-B1 (2026-08-05): jeden odczyt „kto patrzy" na całą Galerię, przekazywany
  // w dół propem. Świadomie NIE wołamy hooka w kafelku: kafelków bywa kilkaset, a każdy
  // zakładałby własny nasłuch tego samego zdarzenia.
  const widokKlienta = useWidokKlienta();
  // O137 (2026-10-20): droga podmiany na oryginały NIE wynika z widoku — właściciel w chmurze ma widok
  // pełny, a stary Photo Match (dysk serwera) jest tam martwy (404). Jedna decyzja dla paska, handlerów i okna.
  const wChmurze = useWChmurze();
  const podmianaPrzegladarka = drogaPhotoMatch(widokKlienta, wChmurze) === 'przegladarka';
  // Studio na telefonie i tablecie (plan §3–§4): układ z szerokości, rozmiar celów z rodzaju wskaźnika.
  const uklad = useUkladEkranu();
  const [kontaOtwarte, setKontaOtwarte] = useState(false);   // szuflada kont i partii (< 1280 px)
  const [wiecejOtwarte, setWiecejOtwarte] = useState(false); // okno „Więcej” paska (telefon)
  const przyciskKontRef = useRef(null);
  // Czym zaczęło się ostatnie wskazanie w siatce: 'mouse' | 'touch' | 'pen'. Rozstrzyga KAŻDE
  // zdarzenie osobno, nie zapytanie o ekran (iPad z klawiaturą ma i palec, i Shift — plan §3).
  const ostatniWskaznik = useRef('mouse');
  const wskaznikCzas = useRef(0);
  // Rodzaj wskaźnika dla TEGO kliknięcia: ważny tylko świeżo po `pointerdown` i zużywany raz —
  // klik bez własnego `pointerdown` (programowy, z czytnika ekranu) liczy się jak mysz, nie jak
  // dawno minione dotknięcie (panel na diffie, 2026-09-30).
  const wskaznikKlikniecia = (e) => {
    const swiezy = e && typeof e.timeStamp === 'number' && Math.abs(e.timeStamp - wskaznikCzas.current) < 1500;
    const typ = swiezy ? ostatniWskaznik.current : 'mouse';
    ostatniWskaznik.current = 'mouse';
    return typ;
  };
  const oknoOtwarteRef = useRef(false);
  oknoOtwarteRef.current = kontaOtwarte || wiecejOtwarte;
  // Obrót / poszerzenie okna: szuflada nie istnieje ≥ 1280 px, „Więcej” poza telefonem — zamknij,
  // żeby po powrocie nie wisiało niewidoczne okno blokujące klawisze i przewijanie.
  useEffect(() => {
    if (!uklad.waski) setKontaOtwarte(false);
    if (!uklad.telefon) setWiecejOtwarte(false);
  }, [uklad.waski, uklad.telefon]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [activeBatchKey, setActiveBatchKey] = useState(null);  // LF-PARTIE-ARCHIWUM: filtr per partia
  const [activeListingId, setActiveListingId] = useState(() => {
    // Sesja 22AO (2026-05-08): parsuj ?source_id= z URL (backward compat z /gallery/?source_id=
    // routing po definitywnym usunięciu Legacy Unified Studio). Przepisywanie pipeline
    // ("Otwórz w Studio" / "Do Studio") nadal otwiera nową kartę z konkretnym ogłoszeniem.
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('source_id') || null;
    } catch { return null; }
  });
  // Sesja 22AP (2026-05-08): ?auto_edit=1 → po załadowaniu photos auto-zaznacz wszystkie
  // z aktywnego source_id i auto-otwórz Edytor — Batch. Używane przez przepisywanie/import
  // buttony "Do Edytora — Batch" / "Otwórz w Edytorze" — operator klika 1× zamiast 3×.
  // Single source — pojedyncze ogłoszenie nie potrzebuje Photo Match.
  const [autoEditPending, setAutoEditPending] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('auto_edit') === '1';
    } catch { return false; }
  });
  // Sesja 22AQ (2026-05-08): ?auto_select=1 + ?source_ids=A,B,C → po załadowaniu photos
  // ZAZNACZ wszystkie zdjęcia z tych N ogłoszeń (NIE auto-redirect do Edytor — Batch).
  // Operator zostaje w Galerii z SelectionBar widocznym → ma dostęp do Photo Match,
  // Edytuj zaznaczone, Eksport, Usuń. Pipeline (multi-source 5+ ogłoszeń) wymaga Photo
  // Match przed Edytor — Batch (matchowanie ORG ↔ studio); auto_select zachowuje ten flow.
  const [autoSelectPending, setAutoSelectPending] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('auto_select') === '1';
    } catch { return false; }
  });
  // Lista source_ids do auto-edit / auto-select. CSV (multi) ALBO pojedyncze source_id (single).
  const [autoEditSourceIds] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const csv = params.get('source_ids');
      if (csv) {
        return new Set(csv.split(',').map(s => decodeURIComponent(s.trim())).filter(Boolean));
      }
      const single = params.get('source_id');
      if (single) return new Set([single]);
      return new Set();
    } catch { return new Set(); }
  });
  const [expandedProfileIds, setExpandedProfileIds] = useState(new Set());
  const [activeTags, setActiveTags] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [lastClickedId, setLastClickedId] = useState(null);
  const [collapsedListings, setCollapsedListings] = useState(new Set());
  const [thumbSize, setThumbSize] = useState(168);
  // Wąski ekran (768–1279 px) ma własny rozmiar miniatur: 130 px daje 5 w rzędzie przy 768 px (plan §1 pkt 6).
  const [thumbWaski, setThumbWaski] = useState(130);
  const [toast, setToast] = useState(null);
  // Sesja 22AU: Photoroom workflow — last import folder (z backend lub eksport tej sesji).
  // Floating "Podmień" button widoczny gdy ustawione (operator wraca z Photoroom, klika).
  const [photoroomLastFolder, setPhotoroomLastFolder] = useState(null);
  // LF-PHOTOROOM-EDYTOR (2026-07-29, druga runda recenzji): otwieranie Findera nie miało
  // ŻADNEJ blokady — seria kliknięć otwierała serię okien. Rygiel w referencji (ten sam
  // wzorzec co w Edytorze — stan Reacta w tym samym takcie jeszcze nie widzi poprzedniego
  // kliknięcia), stan tylko do wizualnego disabled.
  const photoroomFolderRygiel = useRef(false);
  const [photoroomFolderBusy, setPhotoroomFolderBusy] = useState(false);
  // B2 (2026-08-05): numer ostatniej paczki wymiany — u klienta zastępuje ścieżkę folderu
  // (nie ma folderu do pokazania). Przychodzi z serwera, więc przeżywa odświeżenie strony.
  const [ostatniaPaczka, setOstatniaPaczka] = useState(null);
  const [paczkaBusy, setPaczkaBusy] = useState(false);

  /* ---------- DATA LOAD ---------- */
  // Sesja 22X: wydzielone do osobnej funkcji zeby refetch po Usun byl prosty.
  // initial=true tylko przy pierwszym fetch (auto-expand profilu, setLoading).
  const loadData = useCallback(async (initial = false) => {
    try {
      const [profData, photosData] = await Promise.all([
        apiGet('/api/profiles'),
        apiGet('/api/gallery/photos'),
      ]);
      setProfiles(profData.profiles || []);
      setPhotos((photosData.photos || []).map(ph => ({
        ...ph,
        id: `${ph.source_id}__${ph.photo_index}`,
      })));
      if (initial && profData.profiles && profData.profiles.length > 0) {
        setExpandedProfileIds(new Set([profData.profiles[0].id]));
      }
      if (initial) setLoading(false);
    } catch (err) {
      if (initial) {
        setError(err.message);
        setLoading(false);
      } else {
        throw err;
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadData(true).then(() => { if (cancelled) return; });
    return () => { cancelled = true; };
  }, [loadData]);

  // Sesja 22AU: na mount pobierz last_import_folder z backendu — jeśli operator
  // robił eksport w poprzedniej sesji, floating "Podmień" pojawi się od razu.
  useEffect(() => {
    let cancelled = false;
    apiGet('/api/photoroom/last-import-folder')
      .then(res => {
        if (cancelled) return;
        if (res?.folder_path) setPhotoroomLastFolder(res.folder_path);
        // B2: u klienta folderu nie ma — jest numer paczki, i to on decyduje, czy
        // przycisk „Wgraj gotowe" ma się w ogóle pojawić.
        if (res?.ostatnia_paczka) setOstatniaPaczka(res.ostatnia_paczka);
      })
      .catch(() => {});  // silent — endpoint nowy, brak last folder = nic nie pokazuj
    return () => { cancelled = true; };
  }, []);

  /* ---------- DERIVED: listings (groups z photos) ---------- */
  const listings = useMemo(() => {
    const m = new Map();
    photos.forEach(ph => {
      if (!m.has(ph.source_id)) {
        m.set(ph.source_id, {
          id: ph.source_id,
          label: ph.product_label || ph.source_id,
          account: ph.account || 'Bez konta',
        });
      }
    });
    return [...m.values()];
  }, [photos]);

  const photoCounts = useMemo(() => {
    const m = {};
    photos.forEach(ph => { m[ph.source_id] = (m[ph.source_id]||0) + 1; });
    return m;
  }, [photos]);

  /* ---------- DERIVED: visible photos ---------- */
  const visiblePhotos = useMemo(() => {
    return photos.filter(ph => {
      if (activeListingId && ph.source_id !== activeListingId) return false;
      if (activeProfileId) {
        const prof = profiles.find(p => p.id === activeProfileId);
        if (prof && !profileMatchesAccount(prof, ph.account)) return false;
      }
      // LF-PARTIE-ARCHIWUM: filtr per partia (ten sam wzorzec co activeProfileId/activeTags —
      // zawęża WIDOK. GOLD operacje działają na `selected`, nie na visiblePhotos).
      if (activeBatchKey && ph.batch_key !== activeBatchKey) return false;
      // LF-MASK-FILTERS: filtr po stanie maski (mask_source). Wiele chipów = OR.
      // 'Lokalne + Pixian' i 'Pixian' celowo się nakładają (pixian ∈ oba) — operator
      // widzi cały batch ORAZ same płatne.
      if (activeTags.length) {
        const ms = ph.mask_source;
        const matches = activeTags.some(t => TAG_MATCHERS[t] && TAG_MATCHERS[t](ms));
        if (!matches) return false;
      }
      return true;
    });
  }, [photos, activeListingId, activeProfileId, activeBatchKey, activeTags, profiles]);

  /* ---------- DERIVED: partie (batche) w zakresie profilu ---------- */
  const batches = useMemo(() => {
    const m = new Map();
    photos.forEach(ph => {
      if (!ph.batch_key) return;
      if (activeProfileId) {
        const prof = profiles.find(p => p.id === activeProfileId);
        if (prof && !profileMatchesAccount(prof, ph.account)) return;
      }
      if (!m.has(ph.batch_key)) m.set(ph.batch_key, { id: ph.batch_key, label: ph.batch_label || ph.batch_key, count: 0, sortTs: '' });
      const e = m.get(ph.batch_key);
      e.count++;
      // Sort chronologiczny po timestamp z source_id (NIE po tekście labelu — dual review #2)
      const tm = (ph.source_id || '').match(/_(\d{8})_(\d{6})$/);
      const ts = tm ? tm[1] + tm[2] : '';
      if (ts > e.sortTs) e.sortTs = ts;
    });
    return [...m.values()].sort((a, b) => b.sortTs.localeCompare(a.sortTs));
  }, [photos, activeProfileId, profiles]);

  const visibleIds = useMemo(() => visiblePhotos.map(p => p.id), [visiblePhotos]);

  /* ---------- GROUPING per listing ---------- */
  const groupedPhotos = useMemo(() => {
    if (activeListingId) {
      const listing = listings.find(l => l.id === activeListingId);
      return [{listing, photos: visiblePhotos}];
    }
    const groups = new Map();
    visiblePhotos.forEach(ph => {
      if (!groups.has(ph.source_id)) groups.set(ph.source_id, []);
      groups.get(ph.source_id).push(ph);
    });
    return [...groups.entries()].map(([sid, photos]) => ({
      listing: listings.find(l => l.id === sid),
      photos,
    })).filter(g => g.listing);
  }, [visiblePhotos, listings, activeListingId]);

  /* ---------- SELECTION ---------- */
  const toggleOne = (id) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handlePhotoClick = (e, photo) => {
    const id = photo.id;
    const wskaznik = wskaznikKlikniecia(e);
    if (e.shiftKey && lastClickedId) {
      const start = visibleIds.indexOf(lastClickedId);
      const end = visibleIds.indexOf(id);
      if (start >= 0 && end >= 0) {
        const [a,b] = start < end ? [start,end] : [end,start];
        const range = visibleIds.slice(a, b+1);
        setSelected(s => {
          const n = new Set(s);
          range.forEach(x => n.add(x));
          return n;
        });
      }
    } else if (e.metaKey || e.ctrlKey) {
      toggleOne(id);
    } else if (wskaznik === 'touch' || wskaznik === 'pen') {
      // Palec: dotknięcie PRZEŁĄCZA to zdjęcie i dokłada kolejne — telefon nie ma Cmd ani Shift.
      toggleOne(id);
    } else {
      setSelected(new Set([id]));
    }
    setLastClickedId(id);
  };

  const selectAllVisible = () => setSelected(new Set(visibleIds));
  const deselectAll = () => setSelected(new Set());
  const invertSelection = () => {
    setSelected(s => {
      const n = new Set();
      visibleIds.forEach(id => { if (!s.has(id)) n.add(id); });
      return n;
    });
  };

  /* ---------- ACTIONS (Faza 1: placeholder toasts) ---------- */
  const fire = (msg, kind='info') => {
    setToast({msg, kind, id: Date.now()});
    setTimeout(() => setToast(t => (t && t.msg === msg) ? null : t), 2400);
  };
  const handleEdit = () => {
    if (widokKlienta) return;   // LF-SAAS Studio-klient: Edytor = operator (serwer i tak odmawia)
    if (selected.size === 0) return fire('Zaznacz zdjęcia do edycji', 'info');
    // Sesja 21D.2: nawigacja do /galeria3/editor.html z photo_refs w localStorage.
    // Dual GRILL 🔴: localStorage (nie sessionStorage) survive nowej karty; TTL 24h cleanup
    // zapobiega wyciekom gdy operator nigdy nie otworzy editor.html z tym UUID.
    const photoRefs = [];
    for (const id of selected) {
      const ph = photos.find(p => p.id === id);
      if (ph) photoRefs.push({source_id: ph.source_id, photo_index: ph.photo_index});
    }
    if (photoRefs.length === 0) {
      return fire('Nie udało się zbudować batcha (photos desynchronized)', 'danger');
    }
    // guard limit 1000 zdjęć — backend LoadBatchPayload max_length=1000 (podniesione
    // z 500 2026-06-08: operator robi przepisywanie 520+ zdjęć naraz). Bez tego edytor
    // pokazywał surowy JSON z Pydantic validation error.
    if (photoRefs.length > 1000) {
      return fire(`Max 1000 zdjęć w batchu (zaznaczono ${photoRefs.length}). Odznacz część zdjęć.`, 'danger');
    }
    // Cleanup starych batchy (>24h) — tylko przekazania, nie ustawienia pod tym samym przedrostkiem
    // (porządki faza 6: dawna pętla kasowała silnik maski, trwające zadanie maski i styl Kolejki).
    const now = Date.now();
    try { globalThis.LFPrzekazanie.sprzatnijStare(localStorage, now); } catch (e) { console.warn('Sprzątanie przekazań pominięte:', e); }
    const uuid = (crypto.randomUUID && crypto.randomUUID()) || (Math.random().toString(36).slice(2) + Date.now().toString(36));
    const key = `lf_batch_${uuid}`;
    try {
      localStorage.setItem(key, JSON.stringify({photoRefs, created: now}));
    } catch (e) {
      return fire('LocalStorage pełny — wyczyść stare dane', 'danger');
    }
    // LF-GALLERY-RETURN: zapamiętaj kontekst galerii — powrót z edytora wraca tu,
    // nie na punkt 0. Tylko ręczne wejście (auto_edit z innych stron NIE zapisuje).
    try {
      sessionStorage.setItem('lf_gallery_return', JSON.stringify({
        activeProfileId,
        activeListingId,
        expandedProfileIds: [...expandedProfileIds],
        activeTags,
        scrollTop: gridRef.current ? gridRef.current.scrollTop : 0,
      }));
    } catch { /* sessionStorage pełny/wyłączony — powrót po staremu, nie blokuj edycji */ }
    window.location.href = `/galeria3/editor.html?batch_id=${uuid}`;
  };

  // LF-WYBIEL-Z-GALERII (2026-08-05): zaznaczone ZDJĘCIA → ekran „Wybiel tło".
  //
  // Przekazujemy NUMERY zdjęć (`photo_index` = slot Studia liczony od zera), a NIE nazwy
  // plików. Powód zmierzony w kodzie, nie przewidywany: lista wysyłkowa ogłoszenia z importu
  // trzyma nazwy oryginałów z telefonu (`IMG_4410.JPG`), nie `studio_00N.jpg` — nazwa
  // z Galerii nie trafiłaby w nic i ekran powiedziałby „nic do zrobienia" przy trzech
  // zaznaczonych zdjęciach. Tłumaczenie numer→nazwa robi serwer, w jednym miejscu.
  //
  // Ten przycisk NIE liczy pieniędzy i nie woła płatnej operacji — front nie przelicza kwot
  // (zasada z sesji 2026-08-04), a płatne rzeczy startują wyłącznie z ekranu, gdzie widać cenę.
  const handleWybielTlo = () => {
    if (selected.size === 0) return fire('Zaznacz zdjęcia do wybielenia', 'info');
    const indeksy = {};
    for (const id of selected) {
      const ph = photos.find(p => p.id === id);
      if (!ph) continue;
      (indeksy[ph.source_id] = indeksy[ph.source_id] || []).push(ph.photo_index);
    }
    const sourceIds = Object.keys(indeksy);
    if (sourceIds.length === 0) {
      return fire('Nie udało się zbudować listy (zdjęcia rozjechały się z widokiem)', 'danger');
    }
    // Sprzątanie starych list (>24h) — ten sam wzór i ten sam powód co przy paczkach edytora:
    // klucza nie kasuje odbiorca (odświeżenie strony ma pokazać tę samą listę), więc bez tego
    // pamięć przeglądarki rosłaby w nieskończoność.
    const now = Date.now();
    const TTL = 24 * 60 * 60 * 1000;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith('lf_whiten_')) {
        try {
          const v = JSON.parse(localStorage.getItem(k));
          if (!v?.ts || (now - v.ts) > TTL) localStorage.removeItem(k);
        } catch { localStorage.removeItem(k); }
      }
    }
    const uuid = (crypto.randomUUID && crypto.randomUUID()) || (Math.random().toString(36).slice(2) + Date.now().toString(36));
    try {
      // Format rozszerzony o `indeksy`; ekran wybielania czyta stary zapis (same
      // identyfikatory z Kolejki) tak jak dotąd.
      localStorage.setItem(`lf_whiten_${uuid}`, JSON.stringify({ source_ids: sourceIds, indeksy, ts: now }));
    } catch (e) {
      return fire('Nie udało się przekazać listy (pamięć przeglądarki pełna)', 'danger');
    }
    window.location.href = `/wybielanie/?lista=${uuid}`;
  };

  // Sesja 22AP (2026-05-08): auto-edit flow — gdy URL ma ?source_id=X&auto_edit=1
  // (przepisywanie/import "Do Edytora — Batch"), po załadowaniu photos auto-otwórz
  // Edytor — Batch z wszystkimi zdjęciami z tego ogłoszenia. Operator klika 1× w
  // przepisywanie zamiast 3× (klik → Galeria → "Edytuj zaznaczone").
  // Sesja 22AQ: rozszerzone o multi-source (?source_ids=A,B,C) z pipeline-bar.
  useEffect(() => {
    if (!autoEditPending) return;
    if (loading) return;
    if (autoEditSourceIds.size === 0) {
      // Brak source_id/source_ids → auto_edit bez sensu, anuluj cicho.
      setAutoEditPending(false);
      return;
    }
    const matching = photos.filter(ph => autoEditSourceIds.has(ph.source_id));
    if (matching.length === 0) {
      // 22AP dual review #1 (Gemini Flash): jeśli photos już załadowane (loading=false)
      // i mimo to brak matching → auto_edit jest niemożliwe (zły source_id w URL).
      // Anuluj cicho zamiast retry przy każdym następnym photos update (polling, refetch).
      if (!loading) {
        setAutoEditPending(false);
      }
      return;
    }
    const photoRefs = matching.map(ph => ({source_id: ph.source_id, photo_index: ph.photo_index}));
    // guard 1000 (DRY z handleEdit). Auto-edit z multi-source moze nazbierać 520+
    // zdjeć (przepisywanie) → fire toast i nie redirectuj.
    if (photoRefs.length > 1000) {
      fire(`Max 1000 zdjęć w batchu (zaznaczono ${photoRefs.length}). Wybierz mniej ogłoszeń.`, 'danger');
      setAutoEditPending(false);
      return;
    }
    // Cleanup starych batchy (>24h) — wspólny sprzątacz jak w handleEdit.
    const now = Date.now();
    try { globalThis.LFPrzekazanie.sprzatnijStare(localStorage, now); } catch (e) { console.warn('Sprzątanie przekazań pominięte:', e); }
    const uuid = (crypto.randomUUID && crypto.randomUUID()) || (Math.random().toString(36).slice(2) + Date.now().toString(36));
    const key = `lf_batch_${uuid}`;
    try {
      localStorage.setItem(key, JSON.stringify({photoRefs, created: now}));
    } catch (e) {
      fire('LocalStorage pełny — wyczyść stare dane', 'danger');
      setAutoEditPending(false);
      return;
    }
    setAutoEditPending(false);
    window.location.href = `/galeria3/editor.html?batch_id=${uuid}`;
  }, [autoEditPending, photos, autoEditSourceIds, loading]);

  // Sesja 22AQ (2026-05-08): auto-select flow — gdy URL ma ?source_ids=A,B,C&auto_select=1
  // (pipeline-bar przy multi-source N ogłoszeń), po załadowaniu photos zaznacz wszystkie
  // zdjęcia z tych ogłoszeń i ZOSTAW operatora w Galerii. SelectionBar pokaże opcje:
  // Photo Match (matchowanie ORG ↔ studio dla wielu ogłoszeń) + "Edytuj zaznaczone" +
  // Eksport / Usuń. Operator sam decyduje co dalej — bez auto-redirectu.
  useEffect(() => {
    if (!autoSelectPending) return;
    if (loading) return;
    if (autoEditSourceIds.size === 0) {
      setAutoSelectPending(false);
      return;
    }
    const matching = photos.filter(ph => autoEditSourceIds.has(ph.source_id));
    if (matching.length === 0) {
      // Photos załadowane ale brak matchu — anuluj cicho (jak auto_edit defensive guard).
      if (!loading) setAutoSelectPending(false);
      return;
    }
    setSelected(new Set(matching.map(ph => ph.id)));
    setAutoSelectPending(false);
    fire(`Zaznaczono ${matching.length} zdjęć z ${autoEditSourceIds.size} ogłoszeń — Photo Match lub Edytuj zaznaczone`, 'success');
  }, [autoSelectPending, photos, autoEditSourceIds, loading]);

  // Sesja 22X: handleDelete podpiety do prawdziwego backendu.
  // Smart dispatch:
  //   - Wszystkie zdjecia listingu zaznaczone -> DELETE /api/listings/{sid}
  //     (kompletne wyczyszczenie: output/ + imported/ + przepisywanie/ + queue + pipeline + projects)
  //   - Czesc zdjec listingu -> POST /api/studio/delete-photos
  //     (tylko studio_NNN.jpg + garment + undo, listing zostaje)
  // Operator zatwierdzil opcja A: oryginaly z originals/{account}/ NIE sa kasowane
  // (Photo Match V2 index zachowany).
  // U122 (2026-10-15, decyzja operatora 2026-10-14): także u KLIENTA — obie trasy idą przez magazyn i do Kosza
  // konta, z izolacją per konto (Z19 a; `tests/test_saas_u122_usuwanie_konto_b.py`). Dawny `if (widokKlienta) return`
  // stał, bo kasowanie pisało na dysk serwera — od Z19 a już nie.
  const handleDelete = async () => {
    if (selected.size === 0) return fire('Zaznacz zdjęcia do usunięcia', 'info');

    // Group selected photos by source_id
    const bySource = new Map();
    for (const id of selected) {
      const ph = photos.find(p => p.id === id);
      if (!ph) continue;
      if (!bySource.has(ph.source_id)) bySource.set(ph.source_id, []);
      bySource.get(ph.source_id).push(ph);
    }
    if (bySource.size === 0) return fire('Brak danych do usunięcia', 'danger');

    // Decide per source: full listing delete vs partial photo delete
    const fullDeletes = []; // [source_id, ...] — wszystkie zdjecia zaznaczone
    const partialDeletes = []; // [{source_id, photo_index}, ...]
    for (const [sid, sel] of bySource.entries()) {
      const totalForSource = photos.filter(p => p.source_id === sid).length;
      if (sel.length >= totalForSource) {
        fullDeletes.push(sid);
      } else {
        for (const ph of sel) partialDeletes.push({source_id: sid, photo_index: ph.photo_index});
      }
    }

    const totalSelected = selected.size;
    const listingsAffected = bySource.size;
    const fullCount = fullDeletes.length;
    const partialCount = partialDeletes.length;

    // Confirm — tura 44: całe ogłoszenia i pojedyncze zdjęcia idą do Kosza (tury 19 i 38), nie w niebyt
    const lines = [
      `Usunąć ${totalSelected} zdjęć z ${listingsAffected} ogłoszeń?`,
      '',
    ];
    if (fullCount > 0) {
      lines.push(`• ${fullCount} ogłoszeń zaznaczonych w całości — znika całe ogłoszenie: zdjęcia, opis i miejsce na listach`);
    }
    if (partialCount > 0) {
      lines.push(`• ${partialCount} zdjęć — tylko te zdjęcia ze Studia (ogłoszenie zostaje)`);
    }
    lines.push('', 'Wszystko idzie do Kosza — przez 24 godziny przywrócisz to w Koszu (menu u góry).');
    if (!window.confirm(lines.join('\n'))) return;

    fire(`Usuwam ${totalSelected} zdjęć…`, 'info');

    try {
      // Wykonaj sekwencyjnie zeby zlapac kazdy blad osobno
      for (const sid of fullDeletes) {
        await apiSend(`/api/listings/${encodeURIComponent(sid)}`, 'DELETE');
      }
      // U122: w chmurze trasa przyjmuje najwyżej 100 pozycji na żądanie (`_USUN_ZDJECIA_MAX`, 422 powyżej) — porcjami.
      // Wspólny dom porcjowania (`LF_API.apiPostPorcjami`, shared/api.js) Studio nie ładuje — stąd ta krótka pętla.
      for (let i = 0; i < partialDeletes.length; i += USUN_ZDJECIA_PORCJA) {
        await apiSend('/api/studio/delete-photos', 'POST', {photos: partialDeletes.slice(i, i + USUN_ZDJECIA_PORCJA)});
      }

      // Refetch + clear selection
      setSelected(new Set());
      setLastClickedId(null);
      // Jesli aktywny listing zostal usuniety calkowicie -> wroc do widoku wszystkich
      if (activeListingId && fullDeletes.includes(activeListingId)) {
        setActiveListingId(null);
      }
      await loadData(false);
      fire(`Usunięto: ${fullCount} ogłoszeń, ${partialCount} zdjęć`, 'info');
    } catch (err) {
      fire(`Błąd usuwania: ${err.message}`, 'danger');
      // U122: część mogła już zejść (wcześniejsze ogłoszenia, porcje) — lista ma pokazać stan po fakcie, nie sprzed.
      try { await loadData(false); } catch (_) { /* odświeżenie nieudane — komunikat błędu już stoi */ }
    }
  };
  // Sesja 22AU (2026-05-09): Photoroom workflow — eksport + podmiana.
  // Operator wybiera Eksport — LF tworzy folder w ~/Downloads/Photoroom/ z timestampem,
  // kopiuje pliki ze zmienionymi nazwami (LF_<listing>_<idx>_<MMDD-HHMMSS>),
  // otwiera Finder na do_wysylki/. Po pracy w Photoroom operator wraca, klika
  // "Podmień" (floating lub w SelectionBar), wskazuje folder z plikami,
  // LF auto-detect alpha (rogi obrazu) i robi Path A (gotowe z tłem) lub
  // Path B (transparent → garment).
  const handleExport = async () => {
    if (widokKlienta) return;   // B119: Eksport do folderu + Finder serwera = operator; klient ma „Pobierz do obróbki”
    if (selected.size === 0) return fire('Zaznacz zdjęcia do eksportu', 'info');
    try {
      fire(`Eksportuję ${selected.size} zdjęć do Photoroom…`, 'info');
      // GRILL P1 fix: photos.find undefined safety — pomijaj jeśli zdjęcie znikło race
      // Backend payload: listing_id + photo_idx (0-based). Frontend pole: photo_index.
      const photo_specs = [];
      for (const id of selected) {
        const p = photos.find(x => x.id === id);
        if (!p) continue;
        photo_specs.push({ listing_id: p.source_id, photo_idx: p.photo_index });
      }
      if (photo_specs.length === 0) {
        return fire('Nic do eksportu (zdjęcia zniknęły?)', 'danger');
      }
      const res = await apiSend('/api/photoroom/export', 'POST', { photo_specs });
      const errCount = (res.errors || []).length;
      if (errCount > 0) {
        fire(`Wyeksportowano ${res.file_count} (z ${errCount} błędami). Finder otwarty.`, 'info');
      } else {
        fire(`Wyeksportowano ${res.file_count} zdjęć. Finder otwarty.`, 'ok');
      }
      // Ustaw last folder żeby pojawił się floating "Podmień"
      setPhotoroomLastFolder(res.z_photoroom_path);
    } catch (e) {
      fire('Błąd eksportu: ' + (e.message || e), 'danger');
    }
  };
  // LF-PHOTOROOM-EDYTOR (2026-07-29, dołożone na zgłoszenie operatora): to samo ogniwo,
  // które dostał Edytor tego samego dnia — otwiera Finder na folderze TEJ sesji eksportu,
  // do którego wklejasz pliki/ZIP-y pobrane z Photoroom. Ścieżkę zna serwer (zapisał ją
  // przy eksporcie), więc front nic tu nie waliduje — po prostu prosi i pokazuje wynik.
  const handleOtworzFolderPhotoroom = async () => {
    if (photoroomFolderRygiel.current) return;
    photoroomFolderRygiel.current = true;
    setPhotoroomFolderBusy(true);
    try {
      const res = await apiSend('/api/photoroom/open-import-folder', 'POST', {});
      const nazwa = String((res && res.path) || '').split('/').filter(Boolean).slice(-2).join('/');
      if (res && res.zrodlo === 'korzen') {
        fire(`Folder tej sesji nie istnieje — otwarty katalog eksportu (${nazwa}).`, 'warn');
      } else {
        fire(`Otwarty folder: ${nazwa} — wklej tam pliki z Photoroom.`, 'ok');
      }
    } catch (e) {
      fire('Nie udało się otworzyć folderu: ' + (e.message || e), 'danger');
    } finally {
      setPhotoroomFolderBusy(false);
      photoroomFolderRygiel.current = false;
    }
  };
  const handlePhotoroomImport = async () => {
    let folder_path = null;
    // 1. Try fresh fetch z backendu
    try {
      const last = await apiGet('/api/photoroom/last-import-folder');
      folder_path = last.folder_path || photoroomLastFolder;
    } catch {
      folder_path = photoroomLastFolder;
    }
    // 2. Folder picker (Chromium showDirectoryPicker / fallback prompt)
    let chosen = null;
    if (window.showDirectoryPicker) {
      try {
        const handle = await window.showDirectoryPicker({ mode: 'read' });
        // showDirectoryPicker zwraca handle, NIE ścieżkę. Operator widzi nazwę
        // folderu w dialogu — wprowadzi pełną ścieżkę przez prompt fallback.
        // (Browser security — nie ujawnia absolute path).
        const inputPath = window.prompt(
          `Wskazałeś folder "${handle.name}". Wpisz pełną ścieżkę:`,
          folder_path || '~/Downloads/Photoroom/'
        );
        chosen = inputPath ? inputPath.trim() : null;
      } catch (e) {
        // User cancel — silent
        if (e.name !== 'AbortError') console.warn('showDirectoryPicker:', e);
      }
    }
    // Fallback: prompt z domyślną ścieżką
    if (!chosen) {
      chosen = window.prompt(
        'Wpisz ścieżkę do folderu z plikami z Photoroom (np. ~/Downloads/Photoroom/LF_Eksport_.../z_photoroom):',
        folder_path || '~/Downloads/Photoroom/'
      );
      if (chosen) chosen = chosen.trim();
    }
    if (!chosen) return;
    try {
      fire('Skanuję folder…', 'info');
      const res = await apiSend('/api/photoroom/import', 'POST', { folder_path: chosen });
      const summary = `A:${res.path_a_count} B:${res.path_b_count}`;
      // LF-PHOTOROOM-KADR (2026-08-05): maska trafiła na zdjęcie po różnicowaniu dla Vinted,
      // czyli w inny kadr. Podmiana się udała, ale operator musi to zobaczyć TERAZ, nie na aukcji.
      const ostrzezenia = res.ostrzezenia || [];
      if (ostrzezenia.length > 0) {
        console.warn('[photoroom] rozjazd kadru:', ostrzezenia);
        fire(`⚠️ ${ostrzezenia.length} zdjęć ma inny kadr niż maska (były już różnicowane dla Vinted). Zastosuj tło i powtórz różnicowanie.`, 'warn');
      }
      if (res.unmatched && res.unmatched.length > 0) {
        fire(`Zaimportowano ${res.matched} (${summary}). Pominięto ${res.unmatched.length} (nie z naszego eksportu).`, 'ok');
      } else if (res.matched === 0) {
        fire('Folder pusty lub zero plików z naszego eksportu.', 'info');
      } else {
        fire(`Zaimportowano ${res.matched} zdjęć (${summary}). Studio odświeżone.`, 'ok');
      }
      // Refresh galerii
      await loadData();
    } catch (e) {
      fire('Błąd importu: ' + (e.message || e), 'danger');
    }
  };
  // B2 (2026-08-05): ta sama droga u KLIENTA — obróbka u jego dostawcy, ale pliki
  // wędrują przez przeglądarkę. Operator ma folder i Findera; klient w chmurze nie ma
  // ani jednego, ani drugiego (Finder otwierałby się na maszynie serwera).
  // Krok 1: zaznacz → „Pobierz do obróbki" (serwer robi paczkę, przeglądarka ściąga ZIP).
  const handlePobierzDoObrobki = async () => {
    if (paczkaBusy) return;
    if (selected.size === 0) return fire('Zaznacz zdjęcia do obróbki', 'info');
    setPaczkaBusy(true);
    try {
      const photo_specs = [];
      for (const id of selected) {
        const p = photos.find(x => x.id === id);
        if (!p) continue;
        photo_specs.push({ listing_id: p.source_id, photo_idx: p.photo_index });
      }
      if (photo_specs.length === 0) { fire('Nic do wysłania (zdjęcia zniknęły?)', 'danger'); return; }
      const res = await apiSend('/api/photoroom/export', 'POST', { photo_specs });
      if (!res.file_count) { fire('Nie udało się przygotować żadnego zdjęcia.', 'danger'); return; }
      // Pobranie idzie przez fetch, a nie przez zwykły odnośnik: w chmurze do żądania musi
      // dojść token logowania (dokłada go `shared/auth.js`, łatając globalny fetch).
      const r = await fetch(`/api/photoroom/paczka/${res.export_id}`, { cache: 'no-store' });
      if (!r.ok) {
        let powod = 'HTTP ' + r.status;
        try { const j = await r.json(); if (j.detail) powod = j.detail; } catch {}
        throw new Error(powod);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zdjecia_do_obrobki_${res.export_id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setOstatniaPaczka(res.export_id);
      fire(`Pobrano ${res.file_count} zdjęć. Obrób je u siebie i wróć z gotowym plikiem.`, 'ok');
    } catch (e) {
      fire('Nie udało się przygotować paczki: ' + (e.message || e), 'danger');
    } finally {
      setPaczkaBusy(false);
    }
  };
  // Krok 2: „Wgraj gotowe" — ZIP z wynikiem wraca na serwer i podmienia zdjęcia.
  // Podmiana dopasowuje pliki po NAZWIE do wszystkich paczek tego konta, więc wynik
  // starszej paczki trafi we właściwe zdjęcia nawet wtedy, gdy wgra się go „do najnowszej".
  const handleWgrajGotowe = async () => {
    if (paczkaBusy) return;
    if (!ostatniaPaczka) return fire('Najpierw pobierz zdjęcia do obróbki.', 'info');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,application/zip';
    input.onchange = async () => {
      const plik = input.files && input.files[0];
      if (!plik) return;
      setPaczkaBusy(true);
      try {
        fire('Wgrywam gotowe zdjęcia…', 'info');
        const dane = new FormData();
        dane.append('file', plik);
        const r = await fetch(`/api/photoroom/paczka/${ostatniaPaczka}`, { method: 'POST', body: dane });
        if (!r.ok) {
          let powod = 'HTTP ' + r.status;
          try { const j = await r.json(); if (j.detail) powod = j.detail; } catch {}
          throw new Error(powod);
        }
        const wynik = await r.json();
        const ostrzezenia = wynik.ostrzezenia || [];
        if (ostrzezenia.length > 0) {
          fire(`⚠️ ${ostrzezenia.length} zdjęć ma inny kadr niż wycięcie — zastosuj tło i sprawdź je.`, 'warn');
        }
        if (wynik.matched === 0) {
          // Cisza tutaj byłaby najgorsza: klient widzi „gotowe" i nie wie, że nic się nie stało.
          fire('Żadne zdjęcie z tego pliku nie pasuje do pobranej paczki — czy nazwy plików nie zmieniły się przy obróbce?', 'warn');
        } else if (wynik.unmatched && wynik.unmatched.length > 0) {
          fire(`Podmieniono ${wynik.matched} zdjęć. Pominięto ${wynik.unmatched.length} (nie z tej paczki).`, 'ok');
        } else {
          fire(`Podmieniono ${wynik.matched} zdjęć.`, 'ok');
        }
        await loadData();
      } catch (e) {
        fire('Nie udało się wgrać: ' + (e.message || e), 'danger');
      } finally {
        setPaczkaBusy(false);
      }
    };
    input.click();
  };
  // Sesja 22X: usuniecie profilu z cala zawartoscia (cascade delete listings).
  // Backend: DELETE /api/profiles/{id}?with_listings=true — kazde ogloszenie kont profilu
  // idzie DO KOSZA (od tury 19: `_delete_listing_internal` → `kosz.ogloszenie_do_kosza`,
  // „Przywróć” przez dobe). Sam wpis profilu znika trwale; przywrocone ogloszenie wraca pod
  // profilem auto-nazwanym jak jego konto (`user_profiles._incremental_sync`). Oryginaly
  // z originals/{account}/ NIE sa kasowane (Photo Match index zachowany).
  const handleDeleteProfile = async (prof, profileListings, profCount) => {
    // Tura 46: dawne „Operacja nieodwracalna” było nieprawdą od tury 19 (ogłoszenia idą do Kosza).
    const lines = [
      `Usunąć profil "${prof.name}" i całą zawartość?`,
      '',
      `• ${profileListings.length} ogłoszeń`,
      `• ${profCount} zdjęć`,
      `• Konta Vinted: ${(prof.vinted_accounts || []).join(', ') || '(brak)'}`,
      '',
      'Ogłoszenia pójdą do Kosza — przez 24 godziny przywrócisz każde (strona startowa → Kosz → „Przywróć”).',
      'Sam profil znika od razu: przywrócone ogłoszenia wrócą pod profilem nazwanym jak ich konto Vinted (osobnym dla każdego konta).',
      // B119 (raport B117 pkt 7): „Photo Match” to narzędzie operatora — klient go nie zna (makieta 6, okno D14).
      widokKlienta ? 'Oryginały z telefonu zostają.' : 'Oryginały z telefonu (Photo Match) zostają.',
    ];
    if (!window.confirm(lines.join('\n'))) return;

    fire(`Usuwam profil "${prof.name}"…`, 'info');
    try {
      const res = await apiSend(`/api/profiles/${encodeURIComponent(prof.id)}?with_listings=true`, 'DELETE');
      const ld = (res.listings_deleted || []).length;
      const lf = (res.listings_failed || []).length;
      // Jesli aktywny profil/listing zostal usuniety -> wroc do widoku wszystkich
      if (activeProfileId === prof.id) setActiveProfileId(null);
      if (activeListingId && (res.listings_deleted || []).includes(activeListingId)) {
        setActiveListingId(null);
      }
      setSelected(new Set());
      await loadData(false);
      if (lf > 0) {
        // B119: u klienta bez „Listings” i „logów” (dziennik serwera widzi tylko operator).
        fire(widokKlienta
          ? `Profil usunięty. Ogłoszenia: ${ld} w Koszu, ${lf} nie udało się usunąć — spróbuj ponownie.`
          : `Profil usunięty. Listings: ${ld} OK, ${lf} błędów (sprawdź logi).`, 'danger');
      } else {
        fire(`Usunięto profil "${prof.name}" + ${ld} ogłoszeń.`, 'info');
      }
    } catch (err) {
      fire(`Błąd usuwania profilu: ${err.message}`, 'danger');
    }
  };

  // Sesja 22U: Photo Match V2 — modal w Galerii (zamiast w Editor multi-batch).
  const [photoMatchOpen, setPhotoMatchOpen] = useState(false);
  const [photoMatchRefs, setPhotoMatchRefs] = useState([]);
  const handlePhotoMatch = () => {
    if (widokKlienta) return;   // LF-SAAS Studio-klient: Photo Match pyta dysk serwera — u klienta nie istnieje
    if (podmianaPrzegladarka) return;   // O137: w chmurze dysku serwera nie ma także właścicielowi (404)
    if (selected.size === 0) return fire('Zaznacz zdjęcia do dopasowania z oryginałami', 'info');
    const refs = [];
    for (const id of selected) {
      const ph = photos.find(p => p.id === id);
      if (ph) refs.push({source_id: ph.source_id, photo_index: ph.photo_index});
    }
    if (refs.length === 0) return fire('Nie udało się zbudować listy zaznaczonych', 'danger');
    setPhotoMatchRefs(refs);
    setPhotoMatchOpen(true);
  };

  // Photo Match PRZEZ PRZEGLĄDARKĘ (2026-09-07) — droga klienta i (O137) właściciela w chmurze;
  // operator na Macu ma `handlePhotoMatch` wyżej.
  const [pmKlientOpen, setPmKlientOpen] = useState(false);
  const [pmKlientRefs, setPmKlientRefs] = useState([]);
  const handlePodmienOryginaly = () => {
    if (!podmianaPrzegladarka) return;   // operator na Macu ma swoją drogę (folder na dysku)
    if (selected.size === 0) return fire('Zaznacz zdjęcia, które chcesz podmienić na oryginały', 'info');
    const refs = [];
    for (const id of selected) {
      const ph = photos.find(p => p.id === id);
      if (ph) refs.push({source_id: ph.source_id, photo_index: ph.photo_index});
    }
    if (refs.length === 0) return fire('Nie udało się zbudować listy zaznaczonych', 'danger');
    setPmKlientRefs(refs);
    setPmKlientOpen(true);
  };

  /* ---------- KEYBOARD ---------- */
  // Focus guard: tylko teksowe inputy/textarea blokuja shortcuts. `range`, `checkbox`,
  // `button` NIE blokuja — operator mogl klikac slider thumbSize i potem nacisnac Esc.
  useEffect(() => {
    const isTextField = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'TEXTAREA' || el.isContentEditable) return true;
      if (tag === 'INPUT') {
        const type = (el.type || 'text').toLowerCase();
        const textTypes = ['text', 'search', 'email', 'password', 'number', 'tel', 'url'];
        return textTypes.includes(type);
      }
      return false;
    };
    const onKey = (e) => {
      if (isTextField()) return;
      // Otwarta szuflada albo „Więcej”: klawisze należą do okna (Escape je zamyka), nie do siatki.
      if (oknoOtwarteRef.current) return;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      // Cmd+A = zaznacz wszystkie widoczne
      if (mod && !e.shiftKey && k === 'a') {
        e.preventDefault();
        selectAllVisible();
        fire(`Zaznaczono wszystkie widoczne (${visibleIds.length})`, 'info');
      }
      // Cmd+Shift+A = odwroc zaznaczenie (Cmd+I bylo intercepted by Safari)
      else if (mod && e.shiftKey && k === 'a') {
        e.preventDefault();
        invertSelection();
        fire('Odwrócono zaznaczenie', 'info');
      }
      // Cmd+. (Cmd+kropka) = GLOWNY sposob odznaczania na macOS — uniwersalny mac cancel.
      // Zwykle Escape i Cmd+Escape mogly byc przechwycone (OS-level / Chrome extensions /
      // Voice Control / dictation) — zostawione jako fallback dla operatorow u ktorych dzialaja.
      else if ((mod && k === '.') || e.key === 'Escape') {
        e.preventDefault();
        if (selected.size > 0) {
          deselectAll();
          fire('Wyczyszczono zaznaczenie', 'info');
        }
      }
      // Delete/Backspace = usun zaznaczone (placeholder toast w Fazie 1)
      else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleDelete();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visibleIds, selected]);

  /* ---------- LASSO DRAG (throttled 50ms per GRILL polish) ---------- */
  const gridRef = useRef(null);

  // LF-GALLERY-RETURN (2026-06-13): powrót z edytora przywraca kontekst galerii
  // (profil/ogłoszenie/filtry/scroll) zamiast startu od zera. Stan zapisuje
  // handleEdit przed redirect; consume-once (klucz kasowany po odczycie).
  // Skip gdy URL-flow (?auto_edit/?auto_select/?source_id) — te mają własny stan.
  useEffect(() => {
    if (loading) return;
    if (autoEditPending || autoSelectPending || autoEditSourceIds.size > 0) return;
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem('lf_gallery_return') || 'null'); } catch {}
    if (!saved) return;
    sessionStorage.removeItem('lf_gallery_return');
    if (saved.activeProfileId) setActiveProfileId(saved.activeProfileId);
    if (saved.activeListingId) setActiveListingId(saved.activeListingId);
    if (Array.isArray(saved.expandedProfileIds) && saved.expandedProfileIds.length > 0) {
      setExpandedProfileIds(new Set(saved.expandedProfileIds));
    }
    if (Array.isArray(saved.activeTags) && saved.activeTags.length > 0) {
      setActiveTags(saved.activeTags);
    }
    if (typeof saved.scrollTop === 'number' && saved.scrollTop > 0) {
      // grid przerenderowuje się po setState wyżej — scroll po 2 klatkach
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (gridRef.current) gridRef.current.scrollTop = saved.scrollTop;
      }));
    }
  }, [loading]);
  const [lasso, setLasso] = useState(null);
  const lassoStart = useRef(null);
  const preDragSelection = useRef(null);

  const handleGridMouseDown = (e) => {
    if (e.target.closest('[data-photo-id]')) return;
    if (e.button !== 0) return;
    // Prostokąt zaznaczania startuje TYLKO od myszy — palec przewija stronę (plan §3).
    // Przeglądarka po dotknięciu dosyła zgodnościowe `mousedown`; rozpoznaje je `pointerdown` przed nim.
    if (ostatniWskaznik.current !== 'mouse') return;
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + gridRef.current.scrollLeft;
    const y = e.clientY - rect.top + gridRef.current.scrollTop;
    lassoStart.current = {x, y};
    preDragSelection.current = (e.metaKey || e.ctrlKey || e.shiftKey) ? new Set(selected) : new Set();
    setLasso({x0:x, y0:y, x, y});
  };

  useEffect(() => {
    if (!lasso) return;
    const onMoveCore = (e) => {
      const rect = gridRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left + gridRef.current.scrollLeft;
      const y = e.clientY - rect.top + gridRef.current.scrollTop;
      setLasso(l => l && ({...l, x, y}));

      const x0 = Math.min(lassoStart.current.x, x);
      const y0 = Math.min(lassoStart.current.y, y);
      const x1 = Math.max(lassoStart.current.x, x);
      const y1 = Math.max(lassoStart.current.y, y);
      const gRect = gridRef.current.getBoundingClientRect();
      const nodes = gridRef.current.querySelectorAll('[data-photo-id]');
      const newly = new Set(preDragSelection.current);
      nodes.forEach(n => {
        const r = n.getBoundingClientRect();
        const nx = r.left - gRect.left + gridRef.current.scrollLeft;
        const ny = r.top - gRect.top + gridRef.current.scrollTop;
        if (nx < x1 && nx + r.width > x0 && ny < y1 && ny + r.height > y0) {
          newly.add(n.getAttribute('data-photo-id'));
        }
      });
      setSelected(newly);
    };
    const onMove = throttle(onMoveCore, 50);
    const onUp = () => {
      setLasso(null);
      lassoStart.current = null;
      preDragSelection.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [lasso]);

  /* ---------- VIEW META ---------- */
  const activeProfile = activeProfileId ? profiles.find(p => p.id === activeProfileId) : null;
  const activeListing = activeListingId ? listings.find(l => l.id === activeListingId) : null;
  const viewTitle = activeListing ? activeListing.label : (activeProfile ? activeProfile.name : 'Wszystkie zdjęcia');
  const viewSub = (() => {
    const tagFilter = activeTags.length ? ` · filtr: ${activeTags.map(t => etykietaFiltra(t, widokKlienta)).join(', ')}` : '';
    if (activeListing) return `${visiblePhotos.length} zdjęć${tagFilter}`;
    if (activeProfile) {
      const profListings = listings.filter(l => profileMatchesAccount(activeProfile, l.account));
      return `${profListings.length} ogłoszeń · ${visiblePhotos.length} zdjęć${tagFilter}`;
    }
    return `${profiles.length} profili · ${listings.length} ogłoszeń · ${visiblePhotos.length} zdjęć${tagFilter}`;
  })();

  /* ---------- RENDER ---------- */
  // P4 (nowy wygląd): wysokości od RODZICA (#root = 100% body, body odejmuje menu) — gołe 100vh
  // dokładałoby wysokość menu i ucinało dół ekranu (plan §2 „JEDEN model układu”).
  if (loading) return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%', flexDirection:'column', gap:14, color:'var(--text-2)'}}>
      <div className="spinner"></div>
      <div>Ładuję Studio…</div>
    </div>
  );

  if (error) return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%', flexDirection:'column', gap:8, color:'var(--text-2)'}}>
      <div style={{color:'var(--danger-text)', fontSize:14, fontWeight:500}}>Błąd ładowania</div>
      <div style={{color:'var(--text-3)', fontSize:12}}>{error}</div>
      <button onClick={() => location.reload()} style={{marginTop:10, background:'var(--panel)', border:'1px solid var(--line)', color:'var(--text)', padding:'6px 12px', borderRadius:6, cursor:'pointer', fontSize:12}}>Spróbuj ponownie</button>
    </div>
  );

  // < 1280 px: telefon ma zawsze 3 kolumny, wąski ekran kolumny wg szerokości (własny suwak).
  const cols = uklad.telefon ? 'repeat(3, minmax(0, 1fr))'
    : uklad.waski ? `repeat(auto-fill, minmax(${thumbWaski}px, 1fr))`
    : `repeat(auto-fill, minmax(${thumbSize}px, 1fr))`;
  const gap = uklad.telefon ? 6 : uklad.waski ? 12 : 16;
  // Wybór w szufladzie kont od razu ją zamyka — widać wynik, nie listę.
  const poWyborze = (fn) => (...a) => { fn(...a); setKontaOtwarte(false); };
  const sidebarProps = {
    profiles, listings, photoCounts, listingCounts: photoCounts, expandedProfileIds,
    onToggleProfile: (pid) => setExpandedProfileIds(s => { const n = new Set(s); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; }),
    activeProfileId, activeListingId, batches, activeBatchKey, widokKlienta,
    onBatchClick: poWyborze((bk) => { setActiveBatchKey(bk); setActiveListingId(null); setSelected(new Set()); }),
    onAllClick: poWyborze(() => { setActiveProfileId(null); setActiveBatchKey(null); setActiveListingId(null); setSelected(new Set()); }),
    onProfileClick: poWyborze((pid) => { setActiveProfileId(pid); setActiveBatchKey(null); setActiveListingId(null); setSelected(new Set()); }),
    onListingClick: poWyborze((lid) => { setActiveListingId(lid); setActiveProfileId(null); setActiveBatchKey(null); setSelected(new Set()); }),
    onDeleteProfile: handleDeleteProfile,
  };
  const oknoOtwarte = kontaOtwarte || wiecejOtwarte;
  const siatkaStyl = !uklad.waski
    ? {flex:1, overflow:'auto', padding:'20px 22px 140px', position:'relative', cursor: lasso ? 'crosshair' : 'default'}
    : {
      // Pod otwartym oknem siatka się nie przewija (plan §4 1g); w bok nigdy.
      flex:1, overflowX:'hidden', overflowY: oknoOtwarte ? 'hidden' : 'auto', overscrollBehavior:'contain',
      padding: uklad.telefon ? '10px 8px calc(150px + env(safe-area-inset-bottom))' : '14px 14px calc(96px + env(safe-area-inset-bottom))',
      position:'relative', cursor: lasso ? 'crosshair' : 'default',
    };

  return (
    <UkladKontekst.Provider value={uklad}>
    <div style={{display:'flex', height:'100%', width:'100%', position:'relative'}}>
      {!uklad.waski && <Sidebar {...sidebarProps}/>}

      <main style={{flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'var(--bg)', position:'relative', ...(uklad.waski ? { minWidth:0 } : null)}}>
        <Toolbar
          activeTags={activeTags}
          onToggleTag={(t) => setActiveTags(a => a.includes(t) ? a.filter(x => x !== t) : [...a, t])}
          onClearTags={() => setActiveTags([])}
          thumbSize={uklad.waski ? thumbWaski : thumbSize}
          onThumbSize={uklad.waski ? setThumbWaski : setThumbSize}
          viewTitle={viewTitle}
          viewSub={viewSub}
          widokKlienta={widokKlienta}
          onOtworzKonta={() => setKontaOtwarte(true)}
          przyciskKontRef={przyciskKontRef}
        />

        <div ref={gridRef} onMouseDown={handleGridMouseDown} onPointerDownCapture={(e) => { ostatniWskaznik.current = e.pointerType || 'mouse'; wskaznikCzas.current = e.timeStamp; }} style={siatkaStyl}>
          {visiblePhotos.length === 0 ? (
            photos.length === 0
              ? <EmptyState msg={widokKlienta
                  /* KON-01 (2026-09-26): klient nie zna „LFH → Pipeline ETL” — mówimy, skąd biorą się zdjęcia. */
                  ? 'Nie masz jeszcze zdjęć. Pobierz ogłoszenia z Vinted wtyczką Selna — pojawią się tutaj.'
                  : 'Brak zdjęć w bazie. Załaduj listingi przez LFH → Pipeline ETL.'}/>
              : <EmptyState onReset={() => { setActiveTags([]); setActiveProfileId(null); setActiveBatchKey(null); setActiveListingId(null); }}/>
          ) : (
            groupedPhotos.map(({listing, photos: gp}) => {
              const isCollapsed = collapsedListings.has(listing.id);
              const groupSelectedCount = gp.filter(p => selected.has(p.id)).length;
              return (
                <section key={listing.id} style={{marginBottom:28}}>
                  {!activeListingId && (
                    <GroupHeader
                      listing={listing}
                      count={gp.length}
                      selectedCount={groupSelectedCount}
                      onSelectAll={() => {
                        const ids = gp.map(p => p.id);
                        const allSelected = ids.every(id => selected.has(id));
                        setSelected(s => {
                          const n = new Set(s);
                          if (allSelected) ids.forEach(id => n.delete(id));
                          else ids.forEach(id => n.add(id));
                          return n;
                        });
                      }}
                      collapsed={isCollapsed}
                      onToggle={() => setCollapsedListings(s => { const n = new Set(s); if (n.has(listing.id)) n.delete(listing.id); else n.add(listing.id); return n; })}
                    />
                  )}
                  {!isCollapsed && (
                    <div style={{display:'grid', gridTemplateColumns:cols, gap}}>
                      {gp.map(ph => (
                        <PhotoCard
                          key={ph.id}
                          photo={ph}
                          selected={selected.has(ph.id)}
                          onClick={handlePhotoClick}
                          size={thumbSize}
                          showProfileBadge={!activeProfileId && !activeListingId}
                          widokKlienta={widokKlienta}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })
          )}

          {lasso && (
            <div style={{
              position:'absolute',
              left: Math.min(lasso.x0, lasso.x), top: Math.min(lasso.y0, lasso.y),
              width: Math.abs(lasso.x - lasso.x0), height: Math.abs(lasso.y - lasso.y0),
              background:'color-mix(in srgb, var(--accent) 12%, transparent)', border:'1px solid var(--accent)',
              borderRadius:2, pointerEvents:'none',
            }}/>
          )}
        </div>

        <SelectionBar
          count={selected.size}
          totalVisible={visiblePhotos.length}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDeselect={deselectAll}
          onExport={handleExport}
          onPhotoMatch={handlePhotoMatch}
          onPodmienOryginaly={handlePodmienOryginaly}
          onWybielTlo={handleWybielTlo}
          onOtworzFolderPhotoroom={handleOtworzFolderPhotoroom}
          photoroomFolderBusy={photoroomFolderBusy}
          onPhotoroomImport={handlePhotoroomImport}
          widokKlienta={widokKlienta}
          podmianaPrzegladarka={podmianaPrzegladarka}
          onPobierzDoObrobki={handlePobierzDoObrobki}
          onWgrajGotowe={handleWgrajGotowe}
          paczkaBusy={paczkaBusy}
          maPaczke={!!ostatniaPaczka}
          wiecejOtwarte={wiecejOtwarte}
          onWiecej={setWiecejOtwarte}
        />

        {/* Sesja 22AU: floating "Podmień z Photoroom" — niezależny od SelectionBar.
            Pokazuje się gdy istnieje folder z poprzedniego eksportu (operator wraca
            z Photoroom — nic nie zaznaczone, ale widzi przycisk akcji).
            LF-SAAS-B1 (2026-08-05): w widoku klienta ZNIKA. Nie chodzi tylko o nazwę marki —
            te przyciski otwierają Finder na dysku maszyny, na której stoi serwer. U klienta
            w chmurze nie ma czego otworzyć, więc przemianowanie ich byłoby obietnicą bez
            pokrycia. Droga „przynieś własne wycięcie" dla klienta to osobna robota
            (backlog B2: pobranie paczki + wgranie ZIP-a przez przeglądarkę). */}
        {!widokKlienta && photoroomLastFolder && selected.size === 0 && (
          <NadDolem style={{
            position:'absolute', right:18, bottom:18, zIndex:18,
            display:'flex', flexDirection:'column', gap:8, alignItems:'stretch',
            // < 1280 px pasek stoi na dole także bez zaznaczenia — para (R11.10, bez zmian) staje nad nim.
            // Telefon: pasek ma stałe dwa rzędy — odstęp jak dotąd. 768–1279 px: pasek w jednym rzędzie — odstęp
            // jak dotąd (72 px); pasek pełnego widoku zawinięty do dwóch rzędów — 18 px nad nim.
            ...(uklad.waski ? { bottom: uklad.telefon
              ? 'calc(128px + env(safe-area-inset-bottom))'
              : `max(calc(72px + env(safe-area-inset-bottom)), calc(var(${ZMIENNA_PASKA}, 0px) + 18px))` } : null),
          }}>
            {/* LF-PHOTOROOM-EDYTOR: to samo ogniwo co w SelectionBar, tylko poza trybem
                zaznaczania — operator wraca z Photoroom bez zaznaczonych zdjęć. */}
            <button
              onClick={handleOtworzFolderPhotoroom}
              disabled={photoroomFolderBusy}
              style={{
                background:'var(--surface-1)',
                color:'var(--text)', fontSize:12.5, fontWeight:500,
                padding:'10px 14px', borderRadius:10,
                border:'1px solid var(--line-2)',
                boxShadow:'var(--shadow)',
                cursor: photoroomFolderBusy ? 'not-allowed' : 'pointer',
                opacity: photoroomFolderBusy ? 0.55 : 1,
                display:'inline-flex', alignItems:'center', gap:7,
                transition:'transform 0.12s, background 0.12s',
                ...(uklad.dotyk ? { minHeight: CEL_DOTYKU } : null),   // pod palcem cel ≥ 44 px (1366 dotyk: było 37)
              }}
              onMouseEnter={e => { if (photoroomFolderBusy) return; e.currentTarget.style.background = 'var(--surface-3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { if (photoroomFolderBusy) return; e.currentTarget.style.background = 'var(--surface-1)'; e.currentTarget.style.transform = 'none'; }}
              title="Otwiera folder tej sesji — wklej tam pliki/ZIP-y pobrane z Photoroom"
            >
              <Icon.Folder size={13}/>
              <span>Otwórz folder</span>
            </button>
            <button
              onClick={handlePhotoroomImport}
              style={{
                background:'var(--surface-1)',
                color:'var(--text)', fontSize:12.5, fontWeight:500,
                padding:'10px 14px', borderRadius:10,
                border:'1px solid var(--line-2)',
                boxShadow:'var(--shadow)',
                cursor:'pointer',
                display:'inline-flex', alignItems:'center', gap:7,
                transition:'transform 0.12s, background 0.12s',
                ...(uklad.dotyk ? { minHeight: CEL_DOTYKU } : null),   // pod palcem cel ≥ 44 px (1366 dotyk: było 37)
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-1)'; e.currentTarget.style.transform = 'none'; }}
              title="Podmień zdjęcia z folderu Photoroom"
            >
              <Icon.Upload size={13}/>
              <span>Podmień z Photoroom</span>
            </button>
          </NadDolem>
        )}

        {/* B2 (2026-08-05): odpowiednik powyższego dla KLIENTA. Powód istnienia jest ten
            sam co u operatora: wynik obróbki wraca po godzinie, więc w chwili powrotu nic
            nie jest zaznaczone i pasek zaznaczenia jest schowany. Bez tego przycisku
            klient musiałby zaznaczyć cokolwiek, żeby zobaczyć drogę powrotną. */}
        {/* < 1280 px ten przycisk mieszka w pasku (wąski) albo w oknie „Więcej” (telefon). */}
        {!uklad.waski && widokKlienta && ostatniaPaczka && selected.size === 0 && (
          <NadDolem style={{position:'absolute', right:18, bottom:18, zIndex:18}}>
            <button
              onClick={handleWgrajGotowe}
              disabled={paczkaBusy}
              style={{
                background:'var(--surface-1)',
                color:'var(--text)', fontSize:12.5, fontWeight:500,
                padding:'10px 14px', borderRadius:10,
                border:'1px solid var(--line-2)',
                boxShadow:'var(--shadow)',
                cursor: paczkaBusy ? 'not-allowed' : 'pointer',
                opacity: paczkaBusy ? 0.55 : 1,
                display:'inline-flex', alignItems:'center', gap:7,
                transition:'transform 0.12s, background 0.12s',
                ...(uklad.dotyk ? { minHeight: CEL_DOTYKU } : null),   // pod palcem cel ≥ 44 px (1366 dotyk: było 37)
              }}
              onMouseEnter={e => { if (paczkaBusy) return; e.currentTarget.style.background = 'var(--surface-3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { if (paczkaBusy) return; e.currentTarget.style.background = 'var(--surface-1)'; e.currentTarget.style.transform = 'none'; }}
              title="Wgraj plik ZIP z gotowymi zdjęciami — podmienią się w Twoich ogłoszeniach"
            >
              <Icon.Upload size={13}/>
              <span>{paczkaBusy ? 'Wgrywam…' : 'Wgraj gotowe'}</span>
            </button>
          </NadDolem>
        )}

        {photoMatchOpen && (
          <PhotoMatchModal
            selectedPhotoRefs={photoMatchRefs}
            onClose={() => setPhotoMatchOpen(false)}
            fire={fire}
            onReplaceComplete={(data) => {
              // Po fizycznej podmianie pliki w output/{source_id}/studio/ sa nowe (full ORG).
              // Galeria cache_utils i przegladarka maja stare thumby — refetch wymusza nowe URL-e
              // z cache-bust timestamp. cache: 'no-store' w apiGet juz ominie browser cache.
              loadData(false).catch(e => fire && fire(`Refetch po Photo Match: ${e.message}`, 'danger'));
            }}
          />
        )}
        {pmKlientOpen && podmianaPrzegladarka && (
          <PhotoMatchKlientModal
            selectedPhotoRefs={pmKlientRefs}
            onClose={() => setPmKlientOpen(false)}
            fire={fire}
            onReplaceComplete={() => {
              loadData(false).catch(e => fire && fire(`Odświeżenie po podmianie: ${e.message}`, 'danger'));
            }}
          />
        )}

        {toast && <Toast toast={toast}/>}
      </main>

      {/* Lista kont i partii < 1280 px: szuflada z boku zamiast stałego panelu 260 px (plan §4 1b, 1g). */}
      {uklad.waski && kontaOtwarte && (
        <OknoWysuwane strona="lewo" etykieta="Konta i partie" onZamknij={() => setKontaOtwarte(false)} powrot={przyciskKontRef}>
          <Sidebar {...sidebarProps} wSzufladzie onZamknij={() => setKontaOtwarte(false)}/>
        </OknoWysuwane>
      )}

      {/* Skróty klawiszowe tylko tam, gdzie jest klawiatura i miejsce: komputer ≥ 1280 px z myszą. */}
      {!uklad.waski && !uklad.dotyk && <KeyboardHints widokKlienta={widokKlienta}/>}
    </div>
    </UkladKontekst.Provider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App/>);

})(); // IIFE end
